// The bot helper: an AI chef that works the kitchen with the same buttons a player presses.
// Everything it does belongs to one of the Delegate wheel's tasks (safety, serving, kettles, blenders, prep,
// boba, clean-up, running), and it does the ones switched on that suit this shift, most urgent first.
// Every job is a generator that yields once per frame; a job that can't go on (no free spot, the item is
// gone, no path) simply stops, and that target is skipped for a few seconds.
import { RECIPES, INGREDIENTS } from './data.js';
import { COUNTER_H } from './stations.js';

const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const blank = () => ({ mx: 0, mz: 0, grab: false, use: false, useHeld: false, dash: false, swap: false, swapHeld: false });
class Abort extends Error {}

const hasApp = (g, app) => g.level.recipes.some((r) => RECIPES[r].app === app);
/**
 * Delegate wheel tasks: everything a bot does belongs to one. Each shows up only on shifts where it means something.
 * Glyph: an emoji, or img:<icon>.
 */
export const DELEGATE_TASKS = [
  { id: 'safety', glyph: 'img:ext', name: 'Safety', desc: 'Puts out fires, and lifts finished kettles off the heat before they burn', relevant: (g) => hasApp(g, 'kettle') || g.twist === 'flare' || g.twist === 'gala' },
  { id: 'serve', glyph: 'img:cup', name: 'Serve', desc: 'Pours finished drinks (waiting by the kettle or jug with a cup as they finish), adds fizz, pearls, seal and straw, serves whoever is closest to leaving, and sets unordered drinks by the bar', relevant: () => true },
  { id: 'kettles', glyph: 'img:kettle', name: 'Kettles', desc: 'Fills kettles for orders (and starts the next tea early when the menu makes it certain), finishes half-filled ones, and puts empty kettles back on the stove', relevant: (g) => hasApp(g, 'kettle') },
  { id: 'blenders', glyph: 'img:blender', name: 'Blenders', desc: 'Fills blender jugs for orders (and starts the next smoothie early when the menu makes it certain), finishes half-filled ones, and puts empty jugs back on their base', relevant: (g) => hasApp(g, 'blender') },
  { id: 'prep', glyph: '🔪', name: 'Prep', desc: 'Chops and grinds what orders need, and finishes anything left half-done on a board', relevant: (g) => g.level.recipes.some((r) => RECIPES[r].ing.some((i) => INGREDIENTS[i].prep)) },
  { id: 'boba', glyph: 'img:bobacup', name: 'Boba', desc: 'Keeps boba cups with pearls ready beside the Sealer', relevant: (g) => g.level.recipes.some((r) => RECIPES[r].boba) },
  { id: 'cleanup', glyph: '🧽', name: 'Clean up', desc: 'Collects and washes dirty cups (first when cups run short), empties sludge at the Zapper, and picks things up off the floor', relevant: () => true },
  { id: 'runner', glyph: '📦', name: 'Runner', desc: 'Catches ingredients off the belt, brings missing ones across teleporters and bridges, throws supplies to teammates, and keeps a bot on each deck when the bridges go', relevant: (g) => g.stations.some((s) => s.kind === 'conveyor') || g.twist === 'teleport' || g.twist === 'bridge' },
];
export const delegateTasks = (g) => DELEGATE_TASKS.filter((t) => t.relevant(g));
/** How bots always move, whatever's switched on (for How to Play). Glyph: an emoji, or img:<icon>. */
export const BOT_WAYS = [
  { glyph: '↔', name: 'Dodging', desc: 'The one on the less urgent job steps aside, and only when that beats a shove' },
  { glyph: '»', name: 'Dashing', desc: 'On long clear runs, when nobody is in the way' },
  { glyph: '✋', name: 'Making room', desc: 'Steps away from a station when a person walks up to use it' },
  { glyph: '➶', name: 'Throwing', desc: 'Tosses ingredients straight into kettles and jugs, and passes to other bots, when that is quicker' },
];
/** A crew slot played by a bot helper: device (offline) or dev (online) 'bot1', 'bot2', ... */
export const isBot = (p) => String(p.dev || p.device || '').startsWith('bot');
/** What kind of crew these chefs make, for keeping best scores apart: 'humans' (all people), 'bots' (all bot helpers), or 'mixed'. */
export function crewKind(chefs) {
  const bots = chefs.filter(isBot).length;
  return !bots ? 'humans' : bots === chefs.length ? 'bots' : 'mixed';
}
/** The most chefs a crew holds, people and bot helpers together. */
export const MAX_CREW = 4;
/** Whether one more person can take a chef: there's an open seat, or a bot to give up its place. */
export const hasRoomForPerson = (crew) => crew.length < MAX_CREW || crew.some(isBot);
/** Before one more person takes a chef: a full crew gives up its last bot for them. False when every chef is already a person. */
export function makeRoomForPerson(crew) {
  if (!hasRoomForPerson(crew)) return false;
  if (crew.length >= MAX_CREW) crew.splice(crew.map(isBot).lastIndexOf(true), 1);
  return true;
}
/**
 * Seats that reopen go back to bot helpers, up to the number this crew keeps (the saved bot count), so a bot that gave
 * up its place for a person returns once that person steps out or leaves. make(dev) builds the new crew entry.
 */
export function refillBots(crew, want, make) {
  let n = 1;
  while (crew.length < MAX_CREW && crew.filter(isBot).length < want) {
    while (crew.some((p) => (p.dev || p.device) === 'bot' + n)) n++;
    crew.push(make('bot' + n));
  }
}

const nearestTo = (list, x, z) => {
  let best = null, bd = 1e9;
  for (const s of list) {
    const d = Math.hypot(s.wx - x, s.wz - z);
    if (d < bd) { bd = d; best = s; }
  }
  return best;
};
/** What's still missing from a mix to become this recipe (null: the mix can't become it). */
function missingFor(recipe, contents) {
  const need = recipe.ing.slice();
  for (const c of contents) {
    const i = need.indexOf(c);
    if (i < 0) return null;
    need.splice(i, 1);
  }
  return need;
}
const isDrinkItem = (it) => (it.kind === 'cup' || it.kind === 'bobacup') && it.drink && it.drink !== 'sludge';
const isSludgy = (it) => (it.kind === 'vessel' && (it.burnt || it.result === 'sludge')) || ((it.kind === 'cup' || it.kind === 'bobacup') && it.drink === 'sludge');

export class Helper {
  constructor(game, chef) {
    this.g = game;
    this.chef = chef;
    this.ctrl = blank();
    this.job = null;
    this.thinkT = 0;
    this.skip = new Map(); // target key -> game time until which it is left alone
    this.path = null;
    this.pathFor = null;
    this.pathT = 0;
    this.dt = 0;
    this.idleT = 0;
    chef.bot = this;
    chef.thought = '';
    if (!game.helperClaims) game.helperClaims = new Set();
  }
  /** What our owner has us doing (each person steers only the bots they own). */
  get on() {
    return this.g.botPrefs(this.chef);
  }

  // ------------------------------------------------------------ the loop
  tick(dt) {
    const g = this.g, c = this.chef;
    this.ctrl = blank();
    this.dt = dt;
    this.wantPad = null; // the teleporter we mean to take this frame, if any
    this.waitPad = null; // the teleporter we're queueing for, if any
    if (g.state !== 'play' || c.respawn > 0 || c.falling > 0) {
      this._stop();
      c.thought = '';
      return;
    }
    const cf = this.catchFor;
    if (cf && (c.held || g.time > cf.until || cf.from.respawn > 0)) this.catchFor = null;
    else if (cf) {
      // a teammate is about to throw us something: stand still, face them, hands ready
      this._face(cf.from.x, cf.from.z);
      c.thought = cf.icon;
      return;
    }
    this._makeRoomForPeople(dt);
    if (this.yieldGen) {
      // another bot asked us to make way: step aside first, then carry on with our job
      if (this.yieldGen.next().done) this.yieldGen = this.yieldFor = null;
      this._guardPads();
      return;
    }
    this.thinkT -= dt;
    if (this.thinkT <= 0) this._think();
    const j = this.job;
    if (!j) {
      // the idle dots only after a real pause (not in the moment between one job and the next)
      if ((this.idleT += dt) > 0.6) c.thought = '';
      this._guardPads();
      return;
    }
    // stepping aside (no icon) counts as idle for the bubble too
    if (j.icon) this.idleT = 0;
    else if ((this.idleT += dt) > 0.6) c.thought = '';
    j.t += dt;
    let ended = false;
    try {
      if (j.t > j.max) throw new Abort('took too long');
      if (j.gen.next().done) {
        this._stop();
        ended = true;
      }
      this._guardPads();
    } catch (e) {
      if (!(e instanceof Abort)) console.warn('helper:', e);
      (this.log = this.log || []).push(j.key + ': ' + e.message);
      if (this.log.length > 30) this.log.shift();
      this.skip.set(j.key, g.time + 5);
      // (forget skips that have run out, so an infinite shift doesn't pile them up for hours)
      if (this.skip.size > 200) for (const [k, until] of this.skip) if (until <= g.time) this.skip.delete(k);
      this._stop();
      ended = true;
    }
    // straight on to the next thing (no pause, and the bubble doesn't blink back to idle)
    if (ended) this._think();
  }
  _think() {
    this.thinkT = 0.25;
    const best = this._choose();
    const j = this.job;
    if (!best) return;
    if (!j) return this._begin(best);
    if (best.key === j.key) return;
    // an urgent job (a fire, a kettle about to burn) interrupts anything; chores (dishes while there are clean cups,
    // tidying) give way to anything more important; jobs that are cheap to drop (prep, fetching an ingredient not yet
    // in hand, boba cups, belt catching) give way to an important job nobody has taken, if we're the one best placed
    const cutIn = best.meta && best.meta.preempt; // a teammate is waiting on this one
    const soft = j.soft || !!(j.meta && j.meta.softIf && j.meta.softIf());
    if (best.prio >= j.prio + 25 || ((soft || cutIn) && best.prio >= (soft && !j.soft ? 47 : j.prio + 1))) return this._begin(best);
    if (this.g._noDrop) return; // (for testing)
    if (best.prio >= 60 && best.prio >= j.prio + 8 && this._droppable(j) && !this._leaveTo(best)) {
      this.g._drops = (this.g._drops || 0) + 1;
      this._begin(best);
    }
  }
  /** Can this job be dropped for something more important without losing much? */
  _droppable(j, c = this.chef) {
    const stop = j && j.meta && j.meta.stop;
    // waiting by a kettle with a cup: fine to drop while it's still a while off
    const far = (v) => v && !v.result && v.need - v.brewed > 2;
    return stop === 'always' || (stop === 'emptyHanded' && !c.held) || (stop === 'early' && far(j.meta.v));
  }
  /**
   * Another bot, free or on a job it could drop, is clearly nearer this task: leave it to them. (Only for a moment:
   * if it's still waiting a second later, it's ours.)
   */
  _leaveTo(task) {
    const g = this.g, c = this.chef, at = task.meta && task.meta.at;
    if (!at) return false;
    const since = (this._deferred = this._deferred || new Map()).get(task.key);
    if (since && g.time - since > 1) return false;
    const mine = Math.hypot(at.wx - c.x, at.wz - c.z);
    for (const h of g.helpers) {
      const o = h.chef;
      if (h === this || o.respawn > 0 || h.yieldGen || h.catchFor || h.awaiting) continue;
      const j = h.job;
      const free = !j || j.soft || (task.prio >= 60 && task.prio >= j.prio + 8 && h._droppable(j));
      if (!free || Math.hypot(at.wx - o.x, at.wz - o.z) > mine - 1.5) continue;
      if (!since) {
        if (this._deferred.size > 200) this._deferred.clear(); // (old deferrals are long settled: an infinite shift runs for hours)
        this._deferred.set(task.key, g.time);
      }
      return true;
    }
    return false;
  }
  _begin(cand) {
    this._stop();
    const claims = [cand.key, ...(cand.claims || [])];
    for (const k of claims) this.g.helperClaims.add(k);
    this.job = { ...cand, claims, t: 0, gen: cand.run() };
    if (cand.icon || this.idleT > 0.6) this.chef.thought = cand.icon;
    this.path = null;
  }
  _stop() {
    if (this.job) {
      for (const k of this.job.claims) this.g.helperClaims.delete(k);
      // let the job tidy up after itself (its finally blocks), unless it's the one running right now
      try {
        this.job.gen.return();
      } catch (e) {}
    }
    this.job = null;
    this._cancelPass();
  }
  /** Let go of a teammate we'd asked to catch for us. */
  _cancelPass() {
    const r = this.passTo;
    if (r && r.bot && r.bot.catchFor && r.bot.catchFor.from === this.chef) r.bot.catchFor = null;
    this.passTo = null;
  }
  /** Mark something as ours for the rest of this job (so another bot leaves it be). */
  _claim(key) {
    if (!this.job || this.job.claims.includes(key)) return;
    this.job.claims.push(key);
    this.g.helperClaims.add(key);
  }
  /** Done with it before the job ends (a board we've finished chopping on): let others have it. */
  _release(key) {
    if (!this.job) return;
    this.job.claims = this.job.claims.filter((k) => k !== key);
    this.g.helperClaims.delete(key);
  }
  _freeSpot(s) {
    if (s) this._release(this._spotKey(s));
  }
  /** Taken by another helper's job (ours don't count). */
  _taken(key) {
    return this.g.helperClaims.has(key) && !(this.job && this.job.claims.includes(key));
  }

  // ------------------------------------------------------------ choosing the next job
  _choose() {
    const out = [];
    const add = (key, prio, icon, run, max = 30, claims = [], soft = false, meta = null) => {
      if ((this.skip.get(key) || 0) > this.g.time || this._taken(key) || claims.some((k) => this._taken(k))) return;
      out.push({ key, prio, icon, run, max, claims, soft, meta });
    };
    const use = (id) => this._use(id);
    if (use('safety')) {
      this._fireJobs(add);
      this._rescueJobs(add);
    }
    this.holdDeck = false;
    const sup = this.supplyFor;
    if (use('runner')) {
      this._deckJobs(add);
      if (sup && sup.to.bot && sup.to.bot.awaiting === this.chef) add('supply', sup.prio, sup.id === 'straw' ? 'img:straw' : `img:ing:${sup.id}:raw`, () => this._supply(sup), 30, [], false, { preempt: true });
      else if (sup) this.supplyFor = null;
      this._beltJobs(add);
      this._ferryJobs(add);
    } else if (sup) this.supplyFor = null;
    if (use('serve')) {
      this._serveJobs(add);
      this._stockJobs(add, 'kettle');
      this._stockJobs(add, 'blender');
    }
    if (use('cleanup')) {
      this._dumpJobs(add);
      this._dirtyJobs(add);
      this._washJobs(add);
      this._tidyJobs(add);
    }
    if (use('prep')) this._prepJobs(add);
    for (const [id, app] of [['kettles', 'kettle'], ['blenders', 'blender']]) {
      if (!use(id)) continue;
      this._reheatJobs(add, app);
      this._fillJobs(add, app);
      this._returnJobs(add, app);
    }
    if (use('boba')) this._bobaJobs(add);
    if (!out.length) this._restJobs(add);
    let best = null;
    for (const cand of out) if (!best || cand.prio > best.prio) best = cand;
    return best;
  }

  /** Is this Delegate task switched on (and does it mean anything on this shift)? */
  _use(id) {
    const g = this.g;
    if (!g._tasksHere || g._tasksHere.level !== g.level) g._tasksHere = { level: g.level, ids: new Set(delegateTasks(g).map((t) => t.id)) };
    return !!this.on[id] && g._tasksHere.ids.has(id);
  }

  // ------------------------------------------------------------ world queries
  _stations(pred) {
    return this.g.stations.filter(pred);
  }
  _near(list) {
    return nearestTo(list, this.chef.x, this.chef.z);
  }
  _waiting() {
    return this.g.guests.filter((q) => q.state === 'wait').sort((a, b) => a.patience - b.patience);
  }
  /** A spot the helper can stand beside. */
  _reachable(s) {
    return DIRS.some(([dx, dy]) => this._walk(s.x + dx, s.y + dy));
  }
  /** A station's "spot" key: another bot heading there to put something down leaves it to us. */
  _spotKey(s) {
    return 'spot:' + s.x + ',' + s.y;
  }
  _spotFree(s) {
    return !this._taken(this._spotKey(s));
  }
  /** Keep this station for ourselves for the rest of the job (a counter, a board, the Sealer...). */
  _takeSpot(s) {
    if (s) this._claim(this._spotKey(s));
    return s;
  }
  /** Keep this item for ourselves for the rest of the job. */
  _takeItem(it) {
    if (it) this._claim('it:' + it.uid);
    return it;
  }
  _itemFree(it) {
    return !this._taken('it:' + it.uid);
  }
  /** An empty counter to set something down on, near a point (skipped, never forced, when there is none). claim: keep it. */
  _freeCounter(x = this.chef.x, z = this.chef.z, skipKey = null, claim = false) {
    const list = this._stations((s) => s.kind === 'counter' && !s.item && !s.fire && this._reachable(s) && this._spotFree(s) && s !== skipKey);
    const s = nearestTo(list, x, z);
    return claim ? this._takeSpot(s) : s;
  }
  /** Where a loose item belongs, if it has a home. */
  _homeFor(it) {
    if (it.kind === 'cup' && it.dirty) return this._near(this.g.sinks);
    if (it.kind === 'cup' && it.isCleanEmpty) return this._near(this.g.racks);
    if (it.kind === 'vessel' && !it.contents.length && !it.burnt) return this._near(this._stations((s) => s.kind === (it.app === 'kettle' ? 'stove' : 'blender') && !s.item && !s.fire && this._spotFree(s)));
    if (it.kind === 'ext') return this._near(this._stations((s) => s.kind === 'ext' && !s.item && this._spotFree(s)));
    if (it.kind === 'straw') return this._near(this._stations((s) => s.kind === 'straws'));
    return null;
  }
  /** Guests still waiting who no finished drink, filled cup nor mix-in-progress is heading for yet. */
  _uncovered(partials = true) {
    const g = this.g, cover = {};
    const bump = (id) => (cover[id] = (cover[id] || 0) + 1);
    const waiting = this._waiting();
    // every drink and mix in the kitchen (not the cups guests are already drinking from)
    const items = [...g.stations.map((s) => s.item), ...g.chefs.map((c) => c.held), ...g.floorItems.map((f) => f.item), ...g.projectiles.map((p) => p.item)];
    items.forEach((it) => {
      if (!it) return;
      if (isDrinkItem(it)) bump(it.drink);
      else if (it.kind === 'vessel' && !it.burnt) {
        if (it.result && it.result !== 'sludge') bump(it.result);
        else if (!it.result && this._planFor(it)) bump(it.plan);
        else if (!it.result && it.contents.length && partials) {
          const q = waiting.find((w) => w.recipe.app === it.app && missingFor(w.recipe, it.contents));
          if (q) bump(q.recipe.id);
        }
      }
    });
    return waiting.filter((q) => {
      if (cover[q.recipe.id] > 0) {
        cover[q.recipe.id]--;
        return false;
      }
      return true;
    });
  }
  /** The waiting guest who most needs this drink (and isn't another helper's delivery, nor a chef's already carrying it to them). */
  _guestFor(recipeId) {
    const theirs = new Set();
    for (const [c, d] of this.g.deliveries()) if (c !== this.chef && d.guest) theirs.add(d.guest);
    return this._waiting().find((q) => q.recipe.id === recipeId && !this._taken('guest:' + q.id) && !theirs.has(q)) || null;
  }
  /** Ingredients already about (on counters and boards, in hands, on the floor) that count towards a need. */
  _onHand(need) {
    const g = this.g;
    const count = (it) => {
      if (it && it.kind === 'ing' && need[it.id] > 0) need[it.id]--;
    };
    for (const s of g.stations) if (s.kind !== 'conveyor') count(s.item);
    for (const c of g.chefs) count(c.held);
    for (const f of g.floorItems) count(f.item);
    return need;
  }
  _barOf(guest) {
    const x = this.g.world.seats[guest.seat];
    return this.g.stations.find((s) => s.kind === 'bar' && s.x === x) || null;
  }

  // ------------------------------------------------------------ always-on jobs
  _fireJobs(add) {
    if (!this.g.stations.some((s) => s.fire)) return;
    // someone else already has the extinguisher out: let them spray
    if (this.g.chefs.some((c) => c !== this.chef && c.held && c.held.kind === 'ext')) return;
    add('fire', 100, 'img:ext', () => this._fightFire(), 45, [], false, { at: this._near(this._stations((s) => s.fire)) });
  }
  /**
   * The bridges are about to retract (the warning's up): someone should be on the deck with the crates (to pass
   * ingredients over) and someone on the deck with the kettles, jugs and bar (to brew, pour and serve). If a deck
   * that needs one has no bot, the best placed one crosses now (never the only bot on the other deck).
   */
  _deckJobs(add) {
    const g = this.g, ev = g.events;
    this.holdDeck = false;
    if (g._noNew || g.twist !== 'bridge' || ev.bridgePhase !== 'ext' || ev.bridgeT > 8 || g.helpers.length < 2) return;
    const decks = this._decks();
    const deckOf = (c) => decks.get(g.worldToTile(c.x, c.z).join(','));
    // which deck has the most of these (by the floor tiles you'd work them from)
    const mostly = (kinds) => {
      const count = {};
      for (const s of g.stations)
        if (kinds.includes(s.kind)) for (const [x, y] of this._standTiles(s, 1)) if (decks.has(x + ',' + y)) count[decks.get(x + ',' + y)] = (count[decks.get(x + ',' + y)] || 0) + 1;
      const k = Object.keys(count).sort((a, b) => count[b] - count[a])[0];
      return k === undefined ? null : +k;
    };
    const wanted = [mostly(['crate'])];
    wanted.push(mostly(['stove', 'blender', 'bar', 'sealer', 'carbonator']));
    const on = (d) => g.helpers.filter((h) => h.chef.respawn <= 0 && deckOf(h.chef) === d);
    // the only bot on a deck that needs one: stay on it now (as if the bridges were already gone: ask for a throw instead)
    const mine = deckOf(this.chef);
    if (wanted.includes(mine) && on(mine).length === 1) this.holdDeck = true;
    if (ev.bridgeT < 4.3) return;
    for (const deck of wanted) {
      if (deck === null || on(deck).length) continue;
      const goals = new Set([...decks].filter(([, d]) => d === deck).map(([k]) => k));
      // the bot with the shortest walk over (and nothing urgent on) goes, unless it's all another deck has
      let best = null, bl = Infinity;
      for (const h of g.helpers) {
        if (h.chef.respawn > 0 || (h.job && h.job.prio >= 60 && h.job.key !== 'cross')) continue;
        const from = deckOf(h.chef);
        if (wanted.some((d) => d !== deck && d === from) && on(from).length < 2) continue;
        const hold = this.holdDeck;
        this.holdDeck = false; // (their walk, not ours)
        const p = this._path(goals, g.worldToTile(h.chef.x, h.chef.z));
        this.holdDeck = hold;
        if (p && p.length < bl) [best, bl] = [h, p.length];
      }
      if (best !== this) continue;
      add('cross', 66, '🌉', () => this._crossTo(goals, deckOf, deck), 10);
      return;
    }
  }
  /** Which deck each floor tile is on (bridges left out). */
  _decks() {
    const g = this.g;
    if (g._decks) return g._decks;
    const d = new Map();
    let n = 0;
    for (let y = 0; y < g.H; y++)
      for (let x = 0; x < g.W; x++) {
        if (d.has(x + ',' + y) || !'.1234'.includes(g.tileAt(x, y).ch)) continue;
        const q = [[x, y]];
        d.set(x + ',' + y, n);
        while (q.length) {
          const [a, b] = q.shift();
          for (const [dx, dy] of DIRS) {
            const t = g.tileAt(a + dx, b + dy), k = a + dx + ',' + (b + dy);
            if (t && '.1234'.includes(t.ch) && !d.has(k)) {
              d.set(k, n);
              q.push([a + dx, b + dy]);
            }
          }
        }
        n++;
      }
    return (g._decks = d);
  }
  *_crossTo(goals, deckOf, deck) {
    yield* this._emptyHands((it) => it.kind === 'ing'); // (an ingredient can come along)
    for (let t = 0; deckOf(this.chef) !== deck && t < 8; t += this.dt) yield* this._stepTowards(() => goals, 'cross');
  }
  /** Came out as some other drink on the way to the one it's meant for (tea + water is plain tea): more is going in. */
  _midway(v) {
    return !!(v.result && v.plan && v.plan !== v.result && this._planFor(v));
  }
  /** Some bot is on its way to pour this kettle / jug (a cup in hand, or off to get one). */
  _pouring(v) {
    return this.g.helpers.some((h) => h.job && h.job.meta && h.job.meta.v === v && /^(pour|stock):/.test(h.job.key)) ||
      this.g.helpers.some((h) => h.job && (h.job.key === 'pour:' + v.uid || h.job.key === 'stock:' + v.uid));
  }
  _rescueJobs(add) {
    for (const v of this.g.vessels) {
      if (v.app !== 'kettle' || !v.result || v.result === 'sludge' || v.burnt || !v.holder || v.holder.kind !== 'stove') continue;
      if (v.over < 8.5 && this._pouring(v)) continue; // (unless they're cutting it too fine)
      // a finished kettle close to catching fire, with nobody pouring it: take it off the heat
      if (v.over > 6.5 || (v.over > 3 && !this._midway(v) && !this._guestFor(v.result) && !this._canStock(v))) add('rescue:' + v.uid, 90, '♨️', () => this._offTheHeat(v), 15, ['it:' + v.uid], false, { at: v.holder });
    }
  }
  /**
   * A drink that's done before anyone has ordered it: pour it into a cup and set it by the bar, ready to hand over
   * the moment someone asks, and the kettle / jug is free for the next one. (Keeps a clean cup spare, and no more
   * drinks waiting than there are seats.)
   */
  _canStock(v, rid = v.result) {
    const g = this.g;
    if (!rid || rid === 'sludge' || v.burnt || !RECIPES[rid] || RECIPES[rid].boba || this._midway(v)) return false;
    if (!g.level.recipes.includes(rid)) return false; // (nobody will ever order it)
    const clean = g.racks.reduce((a, r) => a + r.clean, 0) + g.stations.filter((s) => s.item && s.item.kind === 'cup' && s.item.isCleanEmpty).length;
    const stocked = g.stations.filter((s) => s.item && isDrinkItem(s.item) && s.kind !== 'bar').length;
    return clean >= 2 && stocked < g.world.seats.length && !!this._freeCounter();
  }
  _stockJobs(add, app) {
    for (const v of this.g.vessels) {
      if (v.app !== app || !v.holder || !v.holder.kind || this._guestFor(v.result) || !this._canStock(v)) continue;
      // sooner when it's sitting on the heat (it'd boil over)
      const hot = v.holder.kind === 'stove' && v.over > 2;
      add('stock:' + v.uid, hot ? 62 : 52, 'img:drink:' + v.result, () => this._stockDrink(v, v.result), 40, ['it:' + v.uid]);
    }
    // still brewing one nobody has ordered: with nothing better to do, have a cup ready by it
    for (const v of this.g.vessels) {
      if (v.app !== app || v.result || v.burnt || v.waiting || !v.contents.length || !this.g._brewing(v)) continue;
      const r = v.plan && RECIPES[v.plan];
      if (!r || (missingFor(r, v.contents) || [0]).length || this._guestFor(r.id) || !this._canStock(v, r.id)) continue;
      add('stock:' + v.uid, 42, 'img:drink:' + r.id, () => this._stockDrink(v, r.id), 60, ['it:' + v.uid], false, { stop: 'early', v });
    }
  }
  *_stockDrink(v, rid) {
    const g = this.g, c = this.chef, r = RECIPES[rid];
    const fits = (it) => it.kind === 'cup' && it.isCleanEmpty;
    if (!(c.held && fits(c.held))) {
      yield* this._emptyHands();
      yield* this._getCup(fits, false, v.holder);
    }
    const cup = c.held;
    // there before it's done: wait by it, then pour the moment it is
    const r0 = RECIPES[rid];
    const brewing = () => !v.result && !v.burnt && (missingFor(r0, v.contents) || [0]).length === 0;
    const at = () => (v.holder && v.holder.kind && (v.result === rid || brewing()) ? v.holder : null);
    const s0 = yield* this._goBeside(at);
    this._face(s0.wx, s0.wz);
    yield* this._wait(() => v.result === rid || !at(), 15, null, at);
    yield* this._press(() => (v.holder && v.holder.kind && v.result === rid ? v.holder : null), () => cup.drink === rid);
    yield* this._finish(cup, r); // fizz, if it's a fizzy one
    // someone's ordered it meanwhile: straight to them
    const q = this._guestFor(rid);
    if (q) return yield* this._deliver(cup, q);
    yield* this._setByBar(cup);
  }
  /** Set a finished drink down by the bar, ready for whoever wants it next. */
  *_setByBar(it) {
    const c = this.chef;
    const bar = this._near(this._stations((s) => s.kind === 'bar'));
    const spot = this._freeCounter(bar ? bar.wx : c.x, bar ? bar.wz : c.z, null, true);
    if (!spot) return yield* this._emptyHands();
    yield* this._press(() => (!spot.item ? spot : null), () => c.held !== it);
    this._freeSpot(spot);
  }
  _serveJobs(add) {
    const c = this.chef;
    // a finished drink already in our hands goes out first, to the guest the delivery share-out gives us; when a nearer
    // chef is carrying the same drink to the only guest who wants it, leave it to them and set ours down by the bar
    if (c.held && isDrinkItem(c.held)) {
      const it = c.held, d = this.g.deliveries().get(c);
      const q = d && d.guest && !this._taken('guest:' + d.guest.id) ? d.guest : this._guestFor(it.drink);
      if (q) add('serve:held' + c.index, 95, 'img:drink:' + it.drink, () => this._serveItem(it, q), 30, ['guest:' + q.id, 'it:' + it.uid]);
      else if (d && !d.guest && d.yield) add('leave:' + it.uid, 60, 'img:drink:' + it.drink, () => this._setByBar(it), 20, ['it:' + it.uid]);
    }
    // finished drinks waiting on counters (even ones still needing fizz, a seal or a straw)
    for (const s of this.g.stations) {
      const it = s.item;
      if (!it || !isDrinkItem(it) || s.kind === 'bar') continue;
      const q = this._guestFor(it.drink);
      if (!q) continue;
      add('serve:' + it.uid, 75 + (1 - q.frac) * 15, 'img:drink:' + it.drink, () => this._serveItem(it, q), 35, ['guest:' + q.id, 'it:' + it.uid], false, { at: s });
    }
    // finished mixes: pour them and take them out (kettles close to burning first)
    for (const v of this.g.vessels) {
      if (!v.result || v.result === 'sludge' || v.burnt || !v.holder || !v.holder.kind) continue;
      if (v.plan && v.plan !== v.result && this._planFor(v) && missingFor(RECIPES[v.plan], v.contents).length) continue;
      const q = this._guestFor(v.result);
      if (!q) continue;
      const hot = v.app === 'kettle' && v.holder.kind === 'stove' ? Math.min(15, v.over * 2) : 0;
      add('pour:' + v.uid, 72 + (1 - q.frac) * 12 + hot, 'img:drink:' + v.result, () => this._serveVessel(v, q), 45, ['guest:' + q.id, 'it:' + v.uid], false, { at: v.holder });
    }
    // still brewing, but done by the time we'd be there with a cup: set off now and pour the moment it's ready
    if (this.g._noEarlyPour) return; // (for testing)
    const boost = this.g.perkActive('boost') ? 2 : 1;
    for (const v of this.g.vessels) {
      if (v.result || v.burnt || v.waiting || !v.contents.length || !this.g._brewing(v)) continue;
      const complete = (r) => r.app === v.app && (missingFor(r, v.contents) || [0]).length === 0;
      const r = v.plan && RECIPES[v.plan] && complete(RECIPES[v.plan]) ? RECIPES[v.plan] : this._waiting().map((q) => q.recipe).find(complete);
      if (!r) continue;
      const q = this._guestFor(r.id);
      if (!q) continue;
      // with nothing better to do, get a cup and wait by it right away; the moment it's nearly done, it's a real pour
      const early = (v.need - v.brewed) / boost > this._pourEta(v, r) + 0.3;
      add('pour:' + v.uid, early ? 44 : 70 + (1 - q.frac) * 12, 'img:drink:' + r.id, () => this._serveVessel(v, q, r.id), 60, early ? ['it:' + v.uid] : ['guest:' + q.id, 'it:' + v.uid], false, { at: v.holder, stop: early ? 'early' : undefined, v });
    }
  }
  /** Roughly how long until we'd be standing at this kettle / jug with the right empty cup (seconds). */
  _pourEta(v, r) {
    const c = this.chef, st = v.holder, walk = (d) => (d * 1.25) / 4;
    const fits = (it) => (r.boba ? it.kind === 'bobacup' && !it.drink && !it.sealed : it.kind === 'cup' && it.isCleanEmpty);
    if (c.held && fits(c.held)) return walk(Math.hypot(st.wx - c.x, st.wz - c.z)) + 0.3;
    const src = this._near(this._stations((s) => (s.item && fits(s.item) && s.kind !== 'bar') || (r.boba ? s.kind === 'cups' : s.kind === 'rack' && s.clean > 0)));
    if (!src) return 99;
    return walk(Math.hypot(src.wx - c.x, src.wz - c.z) + Math.hypot(st.wx - src.wx, st.wz - src.wz)) + 0.8;
  }
  _dumpJobs(add) {
    const loose = [...this.g.stations.filter((s) => s.item && s.kind !== 'bar').map((s) => s.item), ...this.g.floorItems.map((f) => f.item)];
    for (const it of loose) {
      if (!isSludgy(it)) continue;
      const f = this.g.floorItems.find((x) => x.item === it);
      add('dump:' + it.uid, 60, '⚡', () => this._dump(it), 30, ['it:' + it.uid], false, { at: f ? { wx: f.x, wz: f.z } : it.holder });
    }
  }
  _dirtyJobs(add) {
    if (!this.g.sinks.length) return;
    const dirty = this.g.stations.filter((s) => s.item && s.item.kind === 'cup' && s.item.dirty);
    if (!dirty.length) return;
    const clean = this._cleanCups();
    // short of cups for the drinks on their way: that's a real job (and, once started, it carries on till there's one
    // to spare: one trip to the sink for a stack beats a trip per cup); otherwise it's something for a quiet moment
    const short = clean < this._cupsWanted();
    add('dirty', !clean ? 76 : short ? 55 : 46, 'img:cup', () => this._collectDirty(), 40, [], !short, { at: this._near(dirty), softIf: () => this._cleanCups() > this._cupsWanted() });
  }
  /** Clean cups to hand: on the racks, or set down empty somewhere. */
  _cleanCups() {
    const g = this.g;
    return g.racks.reduce((a, r) => a + r.clean, 0) + g.stations.filter((s) => s.item && s.item.kind === 'cup' && s.item.isCleanEmpty && s.kind !== 'bar').length;
  }
  /** Cups about to be needed: a drink brewing or done in every kettle / jug, plus guests nobody's making one for. */
  _cupsWanted() {
    const g = this.g;
    const mixes = g.vessels.filter((v) => !v.burnt && v.result !== 'sludge' && v.contents.length && !(v.result && RECIPES[v.result] && RECIPES[v.result].boba)).length;
    return Math.max(1, mixes + this._uncovered().filter((q) => !q.recipe.boba).length);
  }
  _washJobs(add) {
    for (const sink of this.g.sinks) {
      if (sink.dirty <= 0 || this.g.chefs.some((c) => c !== this.chef && c.job === sink)) continue;
      const clean = this._cleanCups();
      const short = clean < this._cupsWanted();
      add('wash:' + sink.x + ',' + sink.y, !clean ? 77 : short ? 55 : 45, '🧽', () => this._wash(sink), 40, [], !short, { at: sink, softIf: () => this._cleanCups() > this._cupsWanted() });
    }
  }
  _tidyJobs(add) {
    // (only with a free counter to put things on: picking something up just to drop it again tidies nothing)
    if (!this._freeCounter()) return;
    for (const f of this.g.floorItems) if (!isSludgy(f.item)) add('tidy:' + f.item.uid, 20, '🧹', () => this._tidyFloor(f.item), 25, ['it:' + f.item.uid], true);
  }
  /** Empty kettles (or jugs) left on counters go back onto a free base: it's what lets the next drink start. */
  _returnJobs(add, app) {
    for (const s of this.g.stations) {
      const v = s.item;
      if (!v || v.kind !== 'vessel' || v.app !== app || v.contents.length || v.burnt || s.kind !== 'counter') continue;
      if (this._homeFor(v)) add('tidy:' + v.uid, 53, 'img:' + v.app, () => this._putAway(v), 25, ['it:' + v.uid]);
    }
  }

  // ------------------------------------------------------------ delegated jobs
  _prepJobs(add) {
    // half-chopped (or ground) things nobody's working on any more: finish them off
    for (const s of this.g.stations) {
      const it = s.item;
      if (!it || it.kind !== 'ing' || it.ready || s.kind !== it.def.prep || this.g.chefs.some((o) => o.job === s)) continue;
      add('finishprep:' + it.uid, 55, 'img:ing:' + it.id + ':raw', () => this._finishPrepped(s), 25, ['it:' + it.uid], false, { stop: 'always' });
    }
    const need = {};
    for (const q of this._uncovered()) for (const id of q.recipe.ing) if (INGREDIENTS[id].prep) need[id] = (need[id] || 0) + 1;
    // anything already about (prepared, on a board, in someone's hands) counts towards it
    this._onHand(need);
    for (const id in need) {
      if (!this._stations((s) => s.kind === INGREDIENTS[id].prep && !s.item && !s.fire && this._spotFree(s)).length) continue;
      // only what we can get at (a gap or a wall may be in the way)
      if (!this._stations((s) => s.kind === 'crate' && s.ingId === id && this._stepsTo(s) < Infinity).length) continue;
      // one slot per ingredient still needed, so two bots can each prep one when two are wanted
      for (let k = 0; k < need[id]; k++) {
        if (this._taken('prep:' + id + '#' + k)) continue;
        add('prep:' + id + '#' + k, 54, 'img:ing:' + id + ':raw', () => this._prepOne(id), 30, [], false, { stop: 'always' });
        break;
      }
    }
  }
  /** A kettle / jug with a mix in it that isn't done, sitting off its base (where it can't brew): put it back on a free one. */
  _reheatJobs(add, app) {
    const g = this.g, base = app === 'kettle' ? 'stove' : 'blender';
    for (const v of g.vessels) {
      if (v.app !== app || !v.contents.length || v.result || v.burnt || !v.holder || !v.holder.kind || v.holder.kind === base) continue;
      const home = this._near(this._stations((s) => s.kind === base && !s.item && !s.fire && this._spotFree(s)));
      if (!home) return;
      add('reheat:' + v.uid, 58, 'img:' + app, () => this._putBack(v, base), 25, ['it:' + v.uid]);
    }
  }
  *_putBack(v, base) {
    yield* this._pickUp(v);
    const home = this._takeSpot(this._near(this._stations((s) => s.kind === base && !s.item && !s.fire && this._spotFree(s))));
    if (!home) return yield* this._emptyHands();
    yield* this._press(() => (!home.item ? home : null), () => this.chef.held !== v);
    this._freeSpot(home);
  }
  /** The recipe this kettle or jug is being filled for (null once that no longer fits what's in it). */
  _planFor(v) {
    if (!v.plan) return null;
    const r = RECIPES[v.plan];
    if (!r || v.burnt || v.result === 'sludge' || !missingFor(r, v.contents)) {
      v.plan = null;
      return null;
    }
    return r;
  }
  /** On shifts with only one drink for this appliance, what's coming next is no secret: that drink. */
  _predictable(app) {
    const menu = this.g.level.recipes.filter((id) => RECIPES[id].app === app);
    return menu.length === 1 ? RECIPES[menu[0]] : null;
  }
  /**
   * Kettles and jugs to fill: mixes already started (finish them), empty ones for orders nobody has covered,
   * and (on predictable shifts) one drink ahead. Every missing ingredient is its own job, so several bots share it.
   * ferry: only mixes already started (the Ferry task).
   */
  _fillJobs(add, app, ferry = false) {
    const g = this.g, seen = g._mixSeen || (g._mixSeen = new Map());
    const base = app === 'kettle' ? 'stove' : 'blender';
    const tasks = [];
    const onBase = (v) => v.holder && v.holder.kind === base;
    // orders a part-made mix could still be claimed for (each one only once)
    const pool = g._noPool ? null : this._uncovered(false);
    const head = this._sharedStart(app);
    for (const v of g.vessels) {
      if (v.app !== app || v.burnt || !v.holder || !v.holder.kind) continue;
      if (v.result && !this._midway(v)) continue;
      let r = this._planFor(v), prio = v.contents.length ? 58 : 56;
      if (!r && v.contents.length) {
        const n = v.contents.join();
        let m = seen.get(v.uid);
        if (!m || m.n !== n) seen.set(v.uid, (m = { n, t: g.time }));
        // someone may still be adding to it: give them a few seconds, unless it's already waiting for the missing one
        // (or it's our own head start, with no one bringing anything to it)
        const ours = head && missingFor(head, v.contents) && !g.helpers.some((h) => h.job && h.job.meta && h.job.meta.uid === v.uid);
        if (!v.waiting && !ours && g.time - m.t < 4) continue;
        const list = pool || this._waiting();
        const i = list.findIndex((w) => w.recipe.app === app && (missingFor(w.recipe, v.contents) || []).length);
        if (i < 0) continue;
        r = list[i].recipe;
        if (pool) pool.splice(i, 1);
      }
      if (r) tasks.push({ v, r, prio, plan: !v.plan });
    }
    if (ferry) {
      for (const t of tasks) if (t.v.contents.length) this._addJobs(add, t.v, t.r, 36, t.plan);
      return;
    }
    // empty kettles / jugs on their bases, nearest first
    const empties = g.vessels
      .filter((v) => v.app === app && onBase(v) && !v.contents.length && !v.result && !v.burnt && !v.plan)
      .sort((a, b) => Math.hypot(a.holder.wx - this.chef.x, a.holder.wz - this.chef.z) - Math.hypot(b.holder.wx - this.chef.x, b.holder.wz - this.chef.z));
    const orders = this._uncovered().filter((w) => w.recipe.app === app);
    let e = 0;
    for (const q of orders) if (e < empties.length) tasks.push({ v: empties[e++], r: q.recipe, prio: 56, plan: true });
    // one drink ahead where the menu makes it certain (no peeking at which guest comes next)
    const only = this._predictable(app);
    if (only && g.timeLeft > 25) {
      // any free kettle / jug left over gets the next one started (up to a drink ahead for every seat at the bar;
      // one that's done before anyone orders it just comes off the heat to wait, so nothing burns)
      let coming = this._countOf(only.id);
      const waiting = this._waiting().filter((q) => q.recipe.id === only.id).length;
      // (a drink ahead for every seat when it's all anyone orders; when it's one drink among others, just the one)
      const alone = g.level.recipes.every((id) => RECIPES[id].app === app);
      while (e < empties.length && coming < waiting + (alone ? g.world.seats.length : 1)) {
        tasks.push({ v: empties[e++], r: only, prio: 51, plan: true });
        coming++;
      }
    }
    // not certain which drink is next, but every one of them needs some of the same things: get those in and brewing
    // (only when that part-mix can't finish as a drink of its own, and just the one kettle / jug at a time)
    const shared = !only && !g._noNew && !g._noShared && !orders.length && e < empties.length && g.timeLeft > 25 ? this._sharedStart(app) : null;
    if (shared && !g.vessels.some((v) => v.app === app && !v.plan && !v.result && v.contents.length)) tasks.push({ v: empties[e++], r: shared, prio: 50, plan: false });
    for (const t of tasks) this._addJobs(add, t.v, t.r, t.prio, t.plan);
  }
  /** The ingredients every drink on this appliance's menu shares (as a pretend recipe), if that's safe to brew early. */
  _sharedStart(app) {
    const menu = this.g.level.recipes.map((id) => RECIPES[id]).filter((r) => r.app === app);
    if (menu.length < 2) return null;
    let common = menu[0].ing.slice();
    for (const r of menu.slice(1)) {
      const left = r.ing.slice();
      common = common.filter((id) => {
        const i = left.indexOf(id);
        if (i < 0) return false;
        left.splice(i, 1);
        return true;
      });
    }
    // (not the water: that goes in last, once it's known which drink it's for)
    common = common.filter((id) => id !== 'water');
    if (!common.length) return null;
    // it mustn't already be a finished drink on its own (it'd come out as that)
    const same = (a, b) => a.length === b.length && a.slice().sort().join() === b.slice().sort().join();
    if (Object.values(RECIPES).some((r) => r.app === app && same(r.ing, common))) return null;
    return { id: null, app, ing: common };
  }
  /** How many of this drink are made or on their way (cups, finished mixes, kettles being filled for it). */
  _countOf(rid) {
    const g = this.g;
    let n = 0;
    const items = [...g.stations.map((s) => s.item), ...g.chefs.map((c) => c.held), ...g.floorItems.map((f) => f.item)];
    for (const it of items) {
      if (!it) continue;
      if (isDrinkItem(it) && it.drink === rid) n++;
      else if (it.kind === 'vessel' && !it.burnt && (it.result === rid || (!it.result && it.plan === rid))) n++;
    }
    return n;
  }
  /** One job per ingredient still missing (minus the ones other bots are already bringing). */
  _addJobs(add, v, r, prio, plan) {
    const miss = missingFor(r, v.contents);
    if (!miss || !miss.length) return;
    const need = {};
    for (const id of miss) need[id] = (need[id] || 0) + 1;
    let busy = {};
    for (const h of this.g.helpers) {
      const m = h.job && h.job.meta;
      if (m && m.uid === v.uid) busy[m.id] = (busy[m.id] || 0) + 1;
    }
    // thrown and still in the air: already on its way
    const g = this.g;
    g._inFlight = (g._inFlight || []).filter((x) => x.until > g.time);
    for (const x of g._inFlight) if (x.uid === v.uid) busy[x.id] = (busy[x.id] || 0) + 1;
    for (const id in need) {
      const free = need[id] - (busy[id] || 0);
      for (let k = 0; k < free; k++) {
        const key = 'add:' + v.uid + ':' + id + '#' + ((busy[id] || 0) + k);
        // water last: a mix that's half there can't finish early as some other drink
        add(key, prio - (id === 'water' ? 1 : 0), `img:${v.app}|img:ing:${id}:raw`, () => {
          if (plan && !v.plan) v.plan = r.id;
          return this._addOne(v, id, r.id || r);
        }, 60, [], false, { uid: v.uid, id, stop: 'emptyHanded' });
      }
    }
  }
  _bobaJobs(add) {
    const ready = this.g.stations.filter((s) => s.item && s.item.kind === 'bobacup' && s.item.pearls && !s.item.drink && !s.item.sealed).length;
    if (ready >= 2) return;
    const sealer = this._near(this._stations((s) => s.kind === 'sealer'));
    if (!sealer || !this.g.stations.some((s) => s.kind === 'cups')) return;
    const spot = this._freeCounter(sealer.wx, sealer.wz);
    if (!spot) return; // nowhere to set one down: skip for now
    if (this.g._noNew || this.g._noBobaPar) return add('boba', 52, 'img:bobacup', () => this._bobaCupReady(sealer), 30, ['spot:' + spot.x + ',' + spot.y], false, { stop: 'emptyHanded' });
    // one job per cup still wanted, so two bots can each ready one (the second only when no other kind of drink
    // is waiting to be started: then that comes first)
    const otherOrders = this._uncovered().some((q) => !q.recipe.boba);
    for (let k = 0; k < (otherOrders ? 1 : 2) - ready; k++) {
      if (this._taken('boba#' + k)) continue;
      add('boba#' + k, 52, 'img:bobacup', () => this._bobaCupReady(sealer), 30, [], false, { stop: 'emptyHanded' });
      return;
    }
  }
  _beltJobs(add) {
    const need = {};
    for (const q of this._uncovered()) for (const id of q.recipe.ing) need[id] = (need[id] || 0) + 1;
    this._onHand(need);
    // only worth catching if there is somewhere to put it
    if (!this._freeCounter()) return;
    const belt = this._stations((s) => s.kind === 'conveyor' && s.item && s.item.kind === 'ing');
    // what other bots are already catching counts too
    for (const s of belt) if (!this._itemFree(s.item) && need[s.item.id] > 0) need[s.item.id]--;
    for (const s of belt) {
      const it = s.item;
      if (!(need[it.id] > 0) || !this._itemFree(it)) continue;
      add('belt:' + it.uid, 34, 'img:ing:' + it.id + ':raw', () => this._catch(it), 20, ['it:' + it.uid], false, { stop: 'emptyHanded' });
      need[it.id]--;
    }
  }
  _ferryJobs(add) {
    this._fillJobs(add, 'kettle', true);
    this._fillJobs(add, 'blender', true);
  }

  /** Nothing to do, but standing right in front of a station with someone about: step aside. */
  _restJobs(add) {
    const g = this.g, c = this.chef;
    const [tx, ty] = g.worldToTile(c.x, c.z);
    const busySpot = (x, y) => DIRS.some(([dx, dy]) => {
      const t = g.tileAt(x + dx, y + dy);
      return t && t.st;
    });
    if (!busySpot(tx, ty)) return;
    if (!g.chefs.some((o) => o !== c && o.respawn <= 0 && Math.hypot(o.x - c.x, o.z - c.z) < 3)) return;
    add('rest' + c.index, -50, '', () => this._stepAside(busySpot), 6, [], true);
  }
  *_stepAside(busySpot) {
    const g = this.g;
    // the nearest open floor tile with no station beside it (and no one else resting there)
    const goals = new Set();
    for (let y = 0; y < g.H; y++)
      for (let x = 0; x < g.W; x++) if (this._walk(x, y) && !busySpot(x, y) && !this._taken('rest:' + x + ',' + y)) goals.add(x + ',' + y);
    if (!goals.size) return;
    const path = this._path(goals);
    if (!path || path.length > 8) return; // nowhere open close by: stay put
    const [gx, gy] = path[path.length - 1];
    this._claim('rest:' + gx + ',' + gy);
    const [wx, wz] = g.tileToWorld(gx, gy);
    yield* this._goToPoint(wx, wz);
  }

  // ------------------------------------------------------------ getting around
  _walk(x, y) {
    const g = this.g, t = g.tileAt(x, y);
    if (!t) return false;
    if (t.ch === '=') return g.bridgeOpen(x, y) && !(g.events.bridgePhase === 'ext' && (g.events.bridgeT < 4 || (this.holdDeck && g.events.bridgeT < 8)));
    return '.1234'.includes(t.ch);
  }
  _pad(x, y) {
    const t = this.g.tileAt(x, y);
    return t && (t.ch === '@' || t.ch === '&') ? this.g.world.teleports.find((p) => p.x === x && p.y === y) : null;
  }
  /** Shortest walk (tile by tile, through teleporters) to any goal tile. */
  _path(goals, from = null) {
    const g = this.g;
    let [sx, sy] = from || g.worldToTile(this.chef.x, this.chef.z);
    if (!this._walk(sx, sy) && !this._pad(sx, sy)) {
      const n = DIRS.map(([dx, dy]) => [sx + dx, sy + dy]).find(([x, y]) => this._walk(x, y));
      if (n) [sx, sy] = n;
    }
    const key = (x, y) => x + ',' + y;
    const prev = new Map([[key(sx, sy), null]]);
    const queue = [[sx, sy]];
    let end = null;
    while (queue.length) {
      const [x, y] = queue.shift();
      if (goals.has(key(x, y))) {
        end = [x, y];
        break;
      }
      for (const [dx, dy] of DIRS) {
        let nx = x + dx, ny = y + dy;
        const pad = this._pad(nx, ny);
        let via = null;
        if (pad) {
          // stepping onto a pad lands you on its partner
          const dest = g.world.teleports.find((p) => p !== pad && p.ch === pad.ch);
          if (!dest) continue;
          via = [nx, ny];
          [nx, ny] = [dest.x, dest.y];
        } else if (!this._walk(nx, ny) || this._avoiding(nx, ny)) continue;
        const k = key(nx, ny);
        if (prev.has(k)) continue;
        prev.set(k, { from: [x, y], via });
        queue.push([nx, ny]);
      }
    }
    if (!end) return null;
    const path = [];
    for (let cur = end; cur; ) {
      path.unshift(cur);
      const p = prev.get(key(cur[0], cur[1]));
      if (!p) break;
      if (p.via) path.unshift(p.via);
      cur = p.from;
    }
    return path;
  }
  /** Walk until standing beside the station (target() may change as a belt item moves), facing it. */
  /** Tiles to stand on to work at a station: right beside it, or (reach > 1, like spraying) anywhere within reach. */
  _standTiles(s, reach) {
    const out = [];
    if (reach <= 1) {
      for (const [dx, dy] of DIRS) out.push([s.x + dx, s.y + dy]);
      return out;
    }
    const r = Math.ceil(reach);
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) if ((dx || dy) && Math.hypot(dx, dy) <= reach) out.push([s.x + dx, s.y + dy]);
    return out;
  }
  *_goBeside(target, reach = 1) {
    const g = this.g, c = this.chef;
    let noPath = 0, stillT = 0, stuck = 0, lastX = c.x, lastZ = c.z, bestLeft = Infinity, noProg = 0;
    for (;;) {
      const s = target();
      if (!s) throw new Abort('target gone');
      const [tx, ty] = g.worldToTile(c.x, c.z);
      const [cx, cz] = g.tileToWorld(tx, ty);
      // beside it: in the middle of the tile, or close enough when someone keeps us from the middle
      const dS = Math.hypot(s.wx - c.x, s.wz - c.z), reachD = Math.max(1.3, reach + 0.2);
      const crowded = g.chefs.some((o) => o !== c && o.respawn <= 0 && Math.hypot(o.x - c.x, o.z - c.z) < 0.9);
      const near = Math.hypot(c.x - cx, c.z - cz) < 0.2 || ((stillT > 0.25 || crowded) && dS < reachD);
      const onStand = this._standTiles(s, reach).some(([x, y]) => x === tx && y === ty);
      if (onStand && this._walk(tx, ty) && near) {
        this._face(s.wx, s.wz);
        return s;
      }
      if (dS < 2.4) this._claimSpotFrom(s, reach);
      yield* this._stepTowards(() => {
        const goals = new Set();
        for (const [x, y] of this._standTiles(s, reach)) if (this._walk(x, y) && !this._avoiding(x, y)) goals.add(x + ',' + y);
        const occupied = new Set(g.chefs.filter((o) => o !== c && o.respawn <= 0).map((o) => g.worldToTile(o.x, o.z).join(',')));
        const free = [...goals].filter((k) => !occupied.has(k));
        return free.length ? new Set(free) : goals;
      }, s);
      if (!this.path) {
        // no way there at all right now (a bridge is out, say): don't stand about, do something else
        if ((noPath += this.dt) > 0.6) throw new Abort('no way there');
        continue;
      }
      noPath = 0;
      if (Math.hypot(c.x - lastX, c.z - lastZ) < 0.01) stillT += this.dt;
      else stillT = 0;
      [lastX, lastZ] = [c.x, c.z];
      // progress is tiles still to go (shoving back and forth with someone isn't progress)
      const left = this.path.length - this.pathI;
      if (left < bestLeft) {
        bestLeft = left;
        noProg = 0;
      } else noProg += this.dt;
      if (noProg > 1.2) {
        // stuck with someone in the way: one of us gives way, or we find a way around them
        if (++stuck > 3) throw new Abort('blocked');
        const o = this._blocker();
        if (o && this._shouldYield(o)) yield* this._giveWay(o);
        else if (o && o.bot) {
          // we have the right of way: the other bot steps aside, and we wait for it
          o.bot.makeWay(c, 'stuck');
          for (let t = 0; t < 0.5; t += this.dt) yield;
        } else this._avoidChefs(); // a person: go round them
        noProg = 0;
        bestLeft = Infinity;
        stillT = 0;
      }
    }
  }
  /**
   * Nearly there, but every spot to work this station from is taken, one of them by a bot washing up (or on
   * anything less important than us): ask it to step aside for a moment. It carries on once we're done.
   */
  _claimSpotFrom(s, reach) {
    const g = this.g, c = this.chef;
    if (g._noSpotAsk || (this.spotAskT || 0) > g.time) return;
    const spots = this._standTiles(s, reach).filter(([x, y]) => this._walk(x, y));
    const at = (o) => g.worldToTile(o.x, o.z).join(',');
    const others = g.chefs.filter((o) => o !== c && o.respawn <= 0);
    if (!spots.length || spots.some(([x, y]) => !others.some((o) => at(o) === x + ',' + y))) return; // one's free
    for (const [x, y] of spots) {
      const o = others.find((q) => at(q) === x + ',' + y);
      const b = o && o.bot;
      // only someone standing there at their work (a bot just walking through will be gone in a moment)
      if (!b || b.yieldGen || b.catchFor || b.awaiting || Math.hypot(o.vx, o.vz) > 0.4) continue;
      if ((b.job && b.job.soft) || this._shouldYield(o) === false) {
        b.makeWay(c, 'spot');
        this.spotAskT = g.time + 1.5;
        return;
      }
    }
  }
  /**
   * A person is coming right up to us while we're washing up (or on some other low-key job): they probably want
   * this spot, so step aside for them.
   */
  _makeRoomForPeople(dt) {
    const g = this.g, c = this.chef, j = this.job;
    // only while we're standing at our work (a bot walking by isn't in anyone's spot)
    if (g._noSpotAsk || this.yieldGen || Math.hypot(c.vx, c.vz) > 0.5 || !(c.job || (j && (j.soft || j.prio < 60)))) {
      this.personT = 0;
      return;
    }
    for (const o of g.chefs) {
      if (o.bot || o === c || o.respawn > 0) continue;
      const rx = c.x - o.x, rz = c.z - o.z, d = Math.hypot(rx, rz);
      if (d > 1.25) continue;
      const toward = (o.vx * rx + o.vz * rz) / d; // how fast they're coming at us
      const facing = (Math.sin(o.face) * rx + Math.cos(o.face) * rz) / d; // looking our way
      // walking right up to us: they want this spot. Just standing about facing us: only after a real moment, and
      // not over and over for the same person
      const coming = toward > 0.8;
      const lingering = d < 1.05 && facing > 0.7 && !((this.madeRoomFor || new Map()).get(o) > g.time);
      if (coming || lingering) {
        if ((this.personT = (this.personT || 0) + dt) > (coming ? 0.3 : 0.9)) {
          this.personT = 0;
          (this.madeRoomFor = this.madeRoomFor || new Map()).set(o, g.time + 6);
          this.makeWay(o, 'person');
        }
        return;
      }
    }
    this.personT = 0;
  }
  /** Another bot wants past: step aside (unless already doing so). */
  makeWay(from, why = '') {
    if (this.yieldGen) return;
    this.yieldFor = from;
    this.yieldWhy = why; // (what asked us: handy when watching them)
    this.g._yieldWhy = this.g._yieldWhy || {};
    this.g._yieldWhy[why] = (this.g._yieldWhy[why] || 0) + 1;
    this.yieldGen = this._giveWay(from);
  }
  /** The chef right in our way, if any. */
  _blocker() {
    const c = this.chef;
    let best = null, bd = 1.2;
    for (const o of this.g.chefs) {
      if (o === c || o.respawn > 0 || o.falling > 0) continue;
      const d = Math.hypot(o.x - c.x, o.z - c.z);
      if (d < bd) {
        bd = d;
        best = o;
      }
    }
    return best;
  }
  /** Between two bots, the one on the less urgent job (or the later one, on a tie) steps aside. People never have to. */
  _shouldYield(o) {
    if (!o.bot) return false;
    const mine = this.job ? this.job.prio : -99, theirs = o.bot.job ? o.bot.job.prio : -99;
    return mine < theirs || (mine === theirs && this.chef.index > o.index);
  }
  /** Get off the other bot's route (out of a narrow aisle if need be), and wait there until it has gone by. */
  *_giveWay(o) {
    const g = this.g, c = this.chef;
    const [ox, oy] = g.worldToTile(o.x, o.z);
    const theirs = new Set(((o.bot && o.bot.path) || []).slice((o.bot && o.bot.pathI) || 0).map(([x, y]) => x + ',' + y));
    theirs.add(ox + ',' + oy);
    if (!o.bot) {
      // a person: we can't know their route, so it's the way they're heading (or facing), up to a wall
      const sp = Math.hypot(o.vx, o.vz), hx = sp > 0.4 ? o.vx / sp : Math.sin(o.face), hz = sp > 0.4 ? o.vz / sp : Math.cos(o.face);
      const [sx, sy] = Math.abs(hx) > Math.abs(hz) ? [Math.sign(hx), 0] : [0, Math.sign(hz)];
      for (let k = 1; k <= 8 && this._walk(ox + sx * k, oy + sy * k); k++) theirs.add(ox + sx * k + ',' + (oy + sy * k));
    }
    const occupied = new Set(g.chefs.filter((x) => x !== c && x.respawn <= 0).map((x) => g.worldToTile(x.x, x.z).join(',')));
    const [tx, ty] = g.worldToTile(c.x, c.z);
    const goals = new Set();
    for (let y = ty - 9; y <= ty + 9; y++)
      for (let x = tx - 9; x <= tx + 9; x++) {
        const k = x + ',' + y;
        if (!this._walk(x, y) || theirs.has(k) || occupied.has(k)) continue;
        // never a spot back past them (we'd have to walk into them to get there): beside us, or on ahead
        const [wx, wz] = g.tileToWorld(x, y);
        if (Math.hypot(wx - o.x, wz - o.z) < Math.hypot(c.x - o.x, c.z - o.z) - 0.3) continue;
        goals.add(k);
      }
    // plan round the other bot, not through it (nor through anyone else)
    this.avoid = new Set([ox + ',' + oy, ...occupied]);
    this.avoidUntil = g.time + 3;
    this.path = null;
    // they only keep the right of way while their job is still the more urgent one (people always keep it)
    const still = () => !o.bot || this._shouldYield(o);
    const route = goals.size ? this._path(goals) : null;
    let spot = route && route.length <= 12 ? route[route.length - 1] : null;
    if (!spot) {
      // nowhere free to step aside to: walk on ahead of them instead (a shove would push us that way anyway, only slower)
      const ax = c.x - o.x, az = c.z - o.z;
      const [sx, sy] = Math.abs(ax) > Math.abs(az) ? [Math.sign(ax) || 1, 0] : [0, Math.sign(az) || 1];
      for (let k = 1; k <= 6 && this._walk(tx + sx * k, ty + sy * k); k++) spot = [tx + sx * k, ty + sy * k];
      this.avoid = new Set([ox + ',' + oy]);
    }
    if (spot) {
      const [gx, gy] = spot;
      const [wx, wz] = g.tileToWorld(gx, gy);
      let t = 0;
      while (Math.hypot(c.x - wx, c.z - wz) > 0.2 && (t += this.dt) < 3 && still()) {
        const [ax, ay] = g.worldToTile(c.x, c.z);
        if (ax === gx && ay === gy) {
          const dx = wx - c.x, dz = wz - c.z, d = Math.hypot(dx, dz);
          this.ctrl.mx = dx / d;
          this.ctrl.mz = dz / d;
          if (!g._noTraffic) this._traffic(null);
          yield;
        } else yield* this._stepTowards(() => new Set([gx + ',' + gy]), 'giveway' + gx + ',' + gy);
      }
    }
    // let them by (until they're clear of us, or a couple of seconds)
    for (let t = 0; t < 2 && Math.hypot(o.x - c.x, o.z - c.z) < 1.2 && still(); t += this.dt) {
      // still on their way (their route runs through here): keep going, off it or on down the aisle, never stand to be shoved
      const here = g.worldToTile(c.x, c.z).join(',');
      const route = o.bot ? new Set((o.bot.path || []).slice(o.bot.pathI || 0).map(([x, y]) => x + ',' + y)) : theirs;
      if (route.has(here) || this._inFront(o)) {
        const spot = this._offTheirWay(o, route);
        if (spot) {
          const [wx, wz] = g.tileToWorld(spot[0], spot[1]);
          const dx = wx - c.x, dz = wz - c.z, d = Math.hypot(dx, dz);
          if (d > 0.05) {
            this.ctrl.mx = dx / d;
            this.ctrl.mz = dz / d;
          }
        }
      }
      yield;
    }
    this.path = null;
    this.yieldFor = null;
  }
  /** Are we right in front of them, in the way they're heading? */
  _inFront(o) {
    const c = this.chef, sp = Math.hypot(o.vx, o.vz);
    if (sp < 0.3) return false;
    const rx = c.x - o.x, rz = c.z - o.z, along = (rx * o.vx + rz * o.vz) / sp, side = Math.abs(rx * o.vz - rz * o.vx) / sp;
    return along > 0 && along < 1.6 && side < 0.7;
  }
  /** The next tile to step onto to get out of their way: a free one beside us off their route, or on ahead away from them. */
  _offTheirWay(o, route) {
    const g = this.g, c = this.chef;
    const [tx, ty] = g.worldToTile(c.x, c.z);
    const taken = new Set(g.chefs.filter((x) => x !== c && x.respawn <= 0).map((x) => g.worldToTile(x.x, x.z).join(',')));
    const ax = c.x - o.x, az = c.z - o.z;
    const [sx, sy] = Math.abs(ax) > Math.abs(az) ? [Math.sign(ax) || 1, 0] : [0, Math.sign(az) || 1];
    for (const [dx, dy] of [[sy, sx], [-sy, -sx]]) {
      const k = tx + dx + ',' + (ty + dy);
      if (this._walk(tx + dx, ty + dy) && !route.has(k) && !taken.has(k)) return [tx + dx, ty + dy];
    }
    return this._walk(tx + sx, ty + sy) ? [tx + sx, ty + sy] : null;
  }
  /** For a moment, plan around the tiles other chefs stand on. */
  _avoidChefs() {
    this.avoid = new Set();
    for (const o of this.g.chefs) if (o !== this.chef && o.respawn <= 0) this.avoid.add(this.g.worldToTile(o.x, o.z).join(','));
    this.avoidUntil = this.g.time + 2.5;
    this.path = null;
  }
  _avoiding(x, y) {
    return !!this.avoid && this.avoidUntil > this.g.time && this.avoid.has(x + ',' + y);
  }
  /** Would pressing grab now act on this station (not a teammate or a floor item in the way)? */
  _aimed(s) {
    const tg = this.g.findTarget(this.chef);
    return !!(tg && tg.station === s);
  }
  /** One frame of walking along the planned path. */
  *_stepTowards(goalsFn, forKey) {
    const g = this.g, c = this.chef;
    this.pathT -= this.dt;
    if (!this.path || this.pathT <= 0 || this.pathFor !== forKey) {
      const goals = goalsFn();
      this.path = goals.size ? this._path(goals) : null;
      // planning round someone: not the long way round when the short way is clear now (they've moved on)
      if (goals.size && this.avoid && this.avoidUntil > g.time && !String(forKey).startsWith('giveway')) {
        const keep = this.avoid;
        this.avoid = null;
        const plain = this._path(goals);
        const there = new Set(g.chefs.filter((o) => o !== c && o.respawn <= 0).map((o) => g.worldToTile(o.x, o.z).join(',')));
        if (plain && (!this.path || this.path.length > plain.length + 8) && !plain.some(([x, y]) => there.has(x + ',' + y))) this.path = plain;
        else this.avoid = keep;
      }
      this.pathFor = forKey;
      this.pathT = 0.4;
      this.pathI = 0;
      this.aim = null;
    }
    if (this.path) {
      const [tx, ty] = g.worldToTile(c.x, c.z);
      const i = this.path.findIndex(([x, y]) => x === tx && y === ty);
      // how far along the path we are (cutting corners can leave us on a tile that isn't on it)
      if (i >= 0) this.pathI = Math.max(this.pathI, i);
      const at = this.pathI;
      let next = this.path[Math.min(at + 1, this.path.length - 1)];
      if (i >= 0 && this._pad(tx, ty) && Math.abs(next[0] - tx) + Math.abs(next[1] - ty) > 1) {
        // on a teleporter, going across: its centre sends us (step off first if it just brought us here)
        next = c.tpLock ? this.path[Math.max(0, i - 1)] : [tx, ty];
        if (c.tpLock && next[0] === tx && next[1] === ty) next = DIRS.map(([dx, dy]) => [tx + dx, ty + dy]).find(([x, y]) => this._walk(x, y)) || next;
      } else if (c.tpLock && this._pad(tx, ty)) {
        // just arrived on a teleporter: step off it first (even if our way goes straight back through it)
        next = this.path[Math.min(at + 1, this.path.length - 1)];
        if (next[0] === tx && next[1] === ty) next = DIRS.map(([dx, dy]) => [tx + dx, ty + dy]).find(([x, y]) => this._walk(x, y)) || next;
      } else {
        // beeline: head straight for the farthest point of the path we can walk to in a straight line
        this.aimT = (this.aimT || 0) - this.dt;
        if (!this.aim || this.aimT <= 0) {
          this.aim = this._farthestInSight(at);
          this.aimT = 0.1;
        }
        if (this.aim && this.path.indexOf(this.aim) > at) next = this.aim;
      }
      this.wantPad = this._pad(next[0], next[1]); // heading onto a teleporter on purpose
      if (this.wantPad && c.tpLock) {
        // it only just brought us here, and our way goes straight back through: it won't send us again until we've
        // been clear of it, so step right off first (not just to the tile's edge and back, over and over)
        const p = g.world.teleports.find((q) => Math.hypot(q.wx - c.x, q.wz - c.z) < 0.9) || this.wantPad;
        let dx = c.x - p.wx, dz = c.z - p.wz, d = Math.hypot(dx, dz);
        if (d < 1e-3) {
          const n = DIRS.map(([a, b]) => [p.x + a, p.y + b]).find(([x, y]) => this._walk(x, y));
          if (n) {
            const [wx, wz] = g.tileToWorld(n[0], n[1]);
            [dx, dz] = [wx - c.x, wz - c.z];
            d = Math.hypot(dx, dz);
          }
        }
        this.wantPad = null;
        this.ctrl.mx = d > 1e-3 ? dx / d : 0;
        this.ctrl.mz = d > 1e-3 ? dz / d : 0;
        yield;
        return;
      }
      if (this.wantPad && !this._padFree(this.wantPad)) {
        // someone's using this teleporter (or standing at the far end): wait a step short of it
        this.wantPad = null;
        this.ctrl.mx = this.ctrl.mz = 0;
        yield;
        return;
      }
      const [wx, wz] = g.tileToWorld(next[0], next[1]);
      const dx = wx - c.x, dz = wz - c.z, d = Math.hypot(dx, dz);
      if (d > 0.02) {
        const k = Math.min(1, d * 3 + 0.25) / d;
        this.ctrl.mx = dx * k;
        this.ctrl.mz = dz * k;
        // look ahead: sort out anyone in the way before we bump into them
        if (!g._noTraffic) this._traffic(goalsFn);
        if (!g._noDash) this._maybeDash(d);
      }
    }
    yield;
  }
  /** A moving chef we'd touch within the next moment if we both keep going as we are. */
  _meeting() {
    const c = this.chef, vx = this.ctrl.mx * 4, vz = this.ctrl.mz * 4;
    for (const o of this.g.chefs) {
      if (o === c || o.respawn > 0 || o.falling > 0 || Math.hypot(o.vx, o.vz) < 0.4) continue;
      const rx = o.x - c.x, rz = o.z - c.z;
      if (Math.hypot(rx, rz) > 2.6) continue;
      const rvx = o.vx - vx, rvz = o.vz - vz, vv = rvx * rvx + rvz * rvz;
      const t = vv > 1e-6 ? Math.max(0, Math.min(0.7, -(rx * rvx + rz * rvz) / vv)) : 0;
      if (Math.hypot(rx + rvx * t, rz + rvz * t) < 0.45) return o;
    }
    return null;
  }
  /** The nearest chef just ahead of us along our way (within reach of a bump), if any. */
  _ahead(dx, dz) {
    const c = this.chef, l = Math.hypot(dx, dz);
    if (l < 0.1) return null;
    dx /= l;
    dz /= l;
    let best = null, bd = 1.7;
    for (const o of this.g.chefs) {
      if (o === c || o.respawn > 0 || o.falling > 0) continue;
      const rx = o.x - c.x, rz = o.z - c.z;
      const along = rx * dx + rz * dz, side = Math.abs(rx * dz - rz * dx);
      if (along > 0.1 && along < bd && side < 0.7) {
        bd = along;
        best = o;
      }
    }
    return best ? { o: best, along: bd, dx, dz } : null;
  }
  /**
   * Traffic manners: the chef on the more urgent job keeps its line and its speed; the one on the less urgent job
   * dodges out of its way, while a dodge is quicker than a shove. On open floor that's a quick sidestep at full speed.
   * In a one-tile aisle the less urgent one backs out to the nearest side spot, unless there's none close by, in which
   * case the more urgent one just shoves through (quicker than a long retreat). People are never asked to move: bots
   * sidestep them on open floor, and in an aisle take a short way round or squeeze past, as with a bot that can't make room.
   */
  _traffic(goalsFn) {
    const g = this.g, c = this.chef, ctrl = this.ctrl;
    const giving = !!this.yieldGen; // we're the one getting out of the way: never ask them to
    const slow = (k) => {
      ctrl.mx *= k;
      ctrl.mz *= k;
    };
    // someone on a more urgent job (or a person) is coming through: sidestep their line, briskly
    if (!giving) {
      const th = this._threat();
      // no room to sidestep: brake for someone crossing or coming at us, but never for someone overtaking from behind
      if (th && !this._dodge(th) && !th.same) slow(0.6);
    }
    this._spacing();
    // an idle bot parked right where we're headed: ask it to move now, rather than squeeze in beside it
    const end = this.path && this.path[this.path.length - 1];
    if (end && !giving)
      for (const o of g.chefs) {
        const b = o.bot;
        if (!b || o === c || o.job || b.yieldGen || b.catchFor || (b.job && !b.job.soft) || Math.hypot(o.vx, o.vz) > 0.4) continue;
        const [ex, ez] = g.tileToWorld(end[0], end[1]);
        if (Math.hypot(o.x - ex, o.z - ez) < 0.9 && Math.hypot(o.x - c.x, o.z - c.z) < 3) b.makeWay(c, 'dest');
      }
    const a = this._ahead(ctrl.mx, ctrl.mz);
    if (!a) {
      this.waitT = 0;
      return;
    }
    const o = a.o, sp = Math.hypot(o.vx, o.vz);
    const theirWay = sp > 0.4 ? (o.vx * a.dx + o.vz * a.dz) / sp : 0; // 1: going our way, -1: coming at us
    const human = !o.bot;
    const weLead = !human && !this._shouldYield(o); // our job is the more urgent one
    if (theirWay > 0.5) {
      // going our way: right on their heels, drop just below their pace
      if (a.along < 0.75) slow(Math.min(1, (sp / 4) * 0.85));
      return;
    }
    if (!this._narrow(a.dx, a.dz, o)) {
      // open floor: a bot on a less urgent job coming at us dodges us, so we hold our line; anyone else, we go round
      if (weLead && sp > 0.4) return;
      this._veer(a, o);
      return;
    }
    // ---- a one-tile aisle
    if (giving) {
      // we're backing out of someone's way and there's a third chef here: wait for them, don't shove
      if (a.along < 0.9) slow(0);
      return;
    }
    if (o.bot && o.bot.yieldGen) {
      // clearing out for us: walking off ahead is handled above (we follow); stuck with nowhere close to go,
      // a shove through is quicker than waiting, so carry on
      if (o.bot.yieldFor === c) return;
      // clearing out for someone else: go round if there's a way, else give them a moment
      if (sp < 0.3 && this.trafficT <= g.time) {
        this.trafficT = g.time + 0.4;
        if (this._rerouteAround(o, goalsFn, 2)) return;
      }
      if (a.along < 0.9) slow(0);
      return;
    }
    if (this.trafficT > g.time) {
      if (!weLead && !human && a.along < 0.8 && sp < 0.4) slow(a.along < 0.72 ? 0 : 0.3); // never lean on a bot who outranks us
      return;
    }
    this.trafficT = g.time + 0.4;
    if (o.bot) {
      if (sp < 0.4 && this._rerouteAround(o, goalsFn, 2)) return;
      if (weLead) {
        // we've the more urgent job: they back out of the aisle for us, and we keep going
        if (!o.bot.catchFor) o.bot.makeWay(c, 'aisle');
        return;
      }
      // they've the more urgent job: coming at us, we back out; busy in the aisle, we wait for them
      if (sp > 0.4 && theirWay < -0.3) this.makeWay(o, 'aisle-back');
      else if (a.along < 0.9) slow(0);
      return;
    }
    // a person in an aisle coming at us (or pressing into us, facing our way): we back out of their way, as for a bot
    // on a more urgent job, rather than push past them
    const rx = c.x - o.x, rz = c.z - o.z, d = Math.hypot(rx, rz) || 1;
    const facingUs = (Math.sin(o.face) * rx + Math.cos(o.face) * rz) / d > 0.6;
    if (!g._noPersonYield && ((sp > 0.4 && theirWay < -0.3) || (facingUs && a.along < 1.3))) {
      this.makeWay(o, 'person-aisle');
      return;
    }
    // a person just standing about in an aisle: a short way round, or squeeze past
    this._rerouteAround(o, goalsFn, 3);
  }
  /** A dash along a long, straight, empty run (already facing that way, nobody near our line, not onto a teleporter). */
  _maybeDash(runLength) {
    const g = this.g, c = this.chef, ctrl = this.ctrl;
    if ((this.dashAt || 0) > g.time || c.dashCD > 0 || this.yieldGen || this.wantPad || runLength < 2.8) return;
    const sp = Math.hypot(ctrl.mx, ctrl.mz);
    if (sp < 0.95) return;
    const dx = ctrl.mx / sp, dz = ctrl.mz / sp;
    let diff = Math.abs(Math.atan2(dx, dz) - c.face) % (Math.PI * 2);
    if (diff > Math.PI) diff = Math.PI * 2 - diff;
    if (diff > 0.12 || Math.hypot(c.vx, c.vz) < 3) return; // up to speed and pointing the right way
    for (const o of g.chefs) {
      if (o === c || o.respawn > 0) continue;
      const rx = o.x - c.x, rz = o.z - c.z, along = rx * dx + rz * dz;
      if (Math.hypot(rx, rz) < 1.5 || (along > -0.5 && along < 4 && Math.abs(rx * dz - rz * dx) < 1.3)) return;
    }
    ctrl.dash = true;
    this.dashAt = g.time + 1.1;
  }
  /** The chef we most need to dodge: moving, on a more urgent job than ours (or a person), and about to pass through us. */
  _threat() {
    const c = this.chef, vx = this.ctrl.mx * 4, vz = this.ctrl.mz * 4;
    let best = null;
    for (const o of this.g.chefs) {
      if (o === c || o.respawn > 0 || o.falling > 0) continue;
      const osp = Math.hypot(o.vx, o.vz);
      if (osp < 0.6 || (o.bot && !this._shouldYield(o))) continue;
      const rx = o.x - c.x, rz = o.z - c.z;
      if (Math.hypot(rx, rz) > 3) continue;
      const rvx = o.vx - vx, rvz = o.vz - vz, vv = rvx * rvx + rvz * rvz;
      const t = vv > 1e-6 ? Math.max(0, Math.min(0.9, -(rx * rvx + rz * rvz) / vv)) : 0;
      const same = o.vx * vx + o.vz * vz > 0.3 * osp * Math.hypot(vx, vz); // heading our way too (overtaking)
      if (Math.hypot(rx + rvx * t, rz + rvz * t) < 0.75 && (!best || t < best.t)) best = { o, t, same };
    }
    return best;
  }
  /** Step sideways off their line (to whichever side we're already on), keeping our pace. False if there's no room. */
  _dodge(th) {
    const g = this.g, c = this.chef, ctrl = this.ctrl, o = th.o;
    const ol = Math.hypot(o.vx, o.vz), ux = o.vx / ol, uz = o.vz / ol;
    const room = (sgn) => {
      const px = -uz * sgn, pz = ux * sgn;
      const [x, y] = g.worldToTile(c.x + px * 0.75, c.z + pz * 0.75);
      return this._walk(x, y) && !this._avoiding(x, y) ? [px, pz] : null;
    };
    const side = (c.x - o.x) * -uz + (c.z - o.z) * ux >= 0 ? 1 : -1;
    const p = room(side) || room(-side);
    if (!p) return false;
    const k = Math.max(0.4, 1 - th.t / 0.9); // the closer it is, the harder the sidestep
    const sp = Math.max(0.9, Math.hypot(ctrl.mx, ctrl.mz));
    const mx = ctrl.mx * (1 - 0.6 * k) + p[0] * k, mz = ctrl.mz * (1 - 0.6 * k) + p[1] * k;
    const l = Math.hypot(mx, mz) || 1;
    ctrl.mx = (mx / l) * sp;
    ctrl.mz = (mz / l) * sp;
    this.g._dodges = (this.g._dodges || 0) + this.dt;
    return true;
  }
  /** Keep a little room from anyone right beside us (walking shoulder to shoulder looks like shoving), same speed. */
  _spacing() {
    const c = this.chef, ctrl = this.ctrl, sp = Math.hypot(ctrl.mx, ctrl.mz);
    if (sp < 0.2) return;
    let px = 0, pz = 0;
    for (const o of this.g.chefs) {
      if (o === c || o.respawn > 0 || o.falling > 0) continue;
      if (o.bot && !this._shouldYield(o) && Math.hypot(o.vx, o.vz) > 0.4) continue; // they'll make room for us
      const dx = c.x - o.x, dz = c.z - o.z, d = Math.hypot(dx, dz);
      if (d > 0.85 || d < 1e-3) continue;
      const k = (0.85 - d) * 2.2;
      px += (dx / d) * k;
      pz += (dz / d) * k;
    }
    if (!px && !pz) return;
    // sideways only (never backwards or braking): drift apart while still heading where we're going
    const qx = -ctrl.mz / sp, qz = ctrl.mx / sp, lat = px * qx + pz * qz;
    const mx = ctrl.mx + qx * lat, mz = ctrl.mz + qz * lat, l = Math.hypot(mx, mz) || 1;
    ctrl.mx = (mx / l) * sp;
    ctrl.mz = (mz / l) * sp;
  }
  /** Is this a one-tile aisle (no room either side), where we are or where they are? */
  _narrow(dx, dz, o) {
    const g = this.g, c = this.chef;
    const [px, py] = Math.abs(dx) > Math.abs(dz) ? [0, 1] : [1, 0];
    const tight = ([x, y]) => !this._walk(x + px, y + py) && !this._walk(x - px, y - py);
    return tight(g.worldToTile(c.x, c.z)) || tight(g.worldToTile(o.x, o.z));
  }
  /** Steer past someone on open floor: to the side away from them (keep right if dead ahead), at full speed. */
  _veer(a, o) {
    const g = this.g, c = this.chef, ctrl = this.ctrl;
    const rx = o.x - c.x, rz = o.z - c.z;
    const lat = rx * -a.dz + rz * a.dx; // + : they're to our left
    let side = lat > 0.05 ? -1 : 1; // away from them (right when they're dead ahead)
    const [tx, ty] = g.worldToTile(c.x, c.z);
    const free = (sgn) => {
      const px = -a.dz * sgn, pz = a.dx * sgn;
      return this._walk(Math.round(tx + px), Math.round(ty + pz)) || this._walk(...g.worldToTile(c.x + px * 0.6 + a.dx * 0.6, c.z + pz * 0.6 + a.dz * 0.6));
    };
    if (!free(side)) side = -side;
    const k = Math.max(0, (1.4 - a.along) / 1.4) * 0.9;
    const mx = ctrl.mx + -a.dz * side * k, mz = ctrl.mz + a.dx * side * k;
    const l = Math.hypot(mx, mz) || 1, sp = Math.hypot(ctrl.mx, ctrl.mz);
    ctrl.mx = (mx / l) * sp;
    ctrl.mz = (mz / l) * sp;
  }
  /** Plan a way that skips the tile this chef is on (if it isn't much longer). */
  _rerouteAround(o, goalsFn, extra = 5) {
    if (!goalsFn) return false; // just finishing a short step: nothing to plan round
    const g = this.g;
    const t = g.worldToTile(o.x, o.z).join(',');
    const left = this.path ? this.path.length - this.pathI : 0;
    const prev = this.avoid, prevT = this.avoidUntil;
    this.avoid = new Set([t]);
    this.avoidUntil = g.time + 2;
    const goals = goalsFn();
    const alt = goals.size ? this._path(goals) : null;
    if (alt && alt.length <= left + extra) {
      this.path = alt;
      this.pathI = 0;
      this.pathT = 0.4;
      this.aim = null;
      return true;
    }
    this.avoid = prev;
    this.avoidUntil = prevT;
    return false;
  }
  /** The farthest path tile (before any teleporter jump) reachable in a straight line from here. */
  _farthestInSight(at) {
    const P = this.path;
    let end = P.length - 1;
    for (let k = at + 1; k < P.length; k++)
      if (Math.abs(P[k][0] - P[k - 1][0]) + Math.abs(P[k][1] - P[k - 1][1]) > 1) {
        end = k - 1; // the pad we step onto: aim no further than it
        break;
      }
    for (let j = end; j > at + 1; j--) if (this._clearTo(P[j])) return P[j];
    return null;
  }
  /** Can the chef walk straight to this tile's centre, keeping its whole body on open floor? */
  _clearTo([gx, gy]) {
    const g = this.g, c = this.chef;
    const [wx, wz] = g.tileToWorld(gx, gy);
    const [sx, sy] = g.worldToTile(c.x, c.z);
    const dx = wx - c.x, dz = wz - c.z, n = Math.max(1, Math.ceil(Math.hypot(dx, dz) / 0.12));
    const R = 0.36, D = 0.26; // a little wider than the chef, so corners are passed with room to spare
    const offs = [[0, 0], [R, 0], [-R, 0], [0, R], [0, -R], [D, D], [D, -D], [-D, D], [-D, -D]];
    const others = g.chefs.filter((o) => o !== c && o.respawn <= 0 && Math.hypot(o.x - c.x, o.z - c.z) < 1 + Math.hypot(dx, dz));
    for (let k = 1; k <= n; k++) {
      const px = c.x + (dx * k) / n, pz = c.z + (dz * k) / n;
      // not brushing past anyone on the way (a straight line that grazes a chef is a bump)
      for (const o of others) if (Math.hypot(o.x - px, o.z - pz) < 0.5) return false;
      for (const [ox, oz] of offs) {
        const [x, y] = g.worldToTile(px + ox, pz + oz);
        if ((x === gx && y === gy) || (x === sx && y === sy)) continue;
        // no counters, gaps, closed bridges, teleporters we don't mean to take, or chefs we're steering round
        if (!this._walk(x, y) || this._avoiding(x, y)) return false;
      }
    }
    return true;
  }
  /** Walk up to a spot on the floor (for picking up loose items). */
  *_goToPoint(x, z) {
    const g = this.g, c = this.chef;
    const [gx, gy] = g.worldToTile(x, z);
    const pad = this._pad(gx, gy);
    if (pad) {
      yield* this._goBeside(() => ({ x: gx, y: gy, wx: x, wz: z }));
      let t = 0;
      // lean in as far as we can without the pad taking us
      while (Math.hypot(c.x - x, c.z - z) > 0.85 && Math.hypot(c.x - pad.wx, c.z - pad.wz) > 0.6 && (t += this.dt) < 1) {
        const dx = x - c.x, dz = z - c.z, d = Math.hypot(dx, dz);
        this.ctrl.mx = (dx / d) * 0.4;
        this.ctrl.mz = (dz / d) * 0.4;
        yield;
      }
      this._face(x, z);
      return;
    }
    let t = 0;
    while (Math.hypot(c.x - x, c.z - z) > 0.45) {
      if ((t += this.dt) > 12) throw new Abort('no way there');
      const [tx, ty] = g.worldToTile(c.x, c.z);
      if (tx === gx && ty === gy) {
        const dx = x - c.x, dz = z - c.z, d = Math.hypot(dx, dz);
        this.ctrl.mx = (dx / d) * 0.6;
        this.ctrl.mz = (dz / d) * 0.6;
        if (!g._noTraffic) this._traffic(null);
        yield;
      } else yield* this._stepTowards(() => new Set([gx + ',' + gy]), 'pt' + gx + ',' + gy);
    }
    this._face(x, z);
  }
  /**
   * One chef through a teleporter pair at a time, and never onto someone: the far pad has to be clear too
   * (it drops you right in its middle). Close to the pad we book the pair until we're clear of both ends.
   */
  _padFree(pad) {
    const g = this.g, c = this.chef;
    const dest = g.world.teleports.find((p) => p !== pad && p.ch === pad.ch);
    const use = (g._padUse = g._padUse || {})[pad.ch];
    this.waitPad = pad.ch;
    if (use && use.by !== c && use.until > g.time) return false;
    for (const o of g.chefs) {
      // bots queueing for this same teleporter don't hold each other up: whoever books it first goes first
      if (o === c || o.respawn > 0 || (o.bot && o.bot.waitPad === pad.ch)) continue;
      if (Math.hypot(o.x - pad.wx, o.z - pad.wz) < 0.8) return false;
      if (dest && Math.hypot(o.x - dest.wx, o.z - dest.wz) < 1.0) return false;
    }
    if (Math.hypot(c.x - pad.wx, c.z - pad.wz) < 1.4) g._padUse[pad.ch] = { by: c, until: g.time + 2 };
    this.waitPad = null;
    return true;
  }
  /** Our booking of a teleporter pair ends once we're clear of both its pads. */
  _releasePads() {
    const g = this.g, c = this.chef, use = g._padUse;
    if (!use) return;
    for (const ch in use) {
      if (use[ch].by !== c) continue;
      const far = g.world.teleports.filter((p) => p.ch === ch).every((p) => Math.hypot(c.x - p.wx, c.z - p.wz) > 1.1);
      if (far || use[ch].until < g.time) delete use[ch];
    }
  }
  /** Keep clear of teleporter pads we don't mean to take (shoves from other chefs included). */
  _guardPads() {
    const c = this.chef, ctrl = this.ctrl;
    this._releasePads();
    for (const p of this.g.world.teleports) {
      if (p === this.wantPad) continue;
      let dx = c.x - p.wx, dz = c.z - p.wz, d = Math.hypot(dx, dz);
      if (d > 0.8) continue;
      if (d < 1e-3) {
        // dead centre (it just brought us here): step off towards open floor, never wait about on a pad
        const n = DIRS.map(([a, b]) => [p.x + a, p.y + b]).find(([x, y]) => this._walk(x, y));
        if (!n) continue;
        const [wx, wz] = this.g.tileToWorld(n[0], n[1]);
        [dx, dz, d] = [wx - c.x, wz - c.z, 1];
      }
      const k = ((0.8 - d) / 0.8) * 1.6; // pushes harder the closer we are to its middle
      ctrl.mx += (dx / d) * k;
      ctrl.mz += (dz / d) * k;
    }
    const l = Math.hypot(ctrl.mx, ctrl.mz);
    if (l > 1) {
      ctrl.mx /= l;
      ctrl.mz /= l;
    }
  }
  _face(x, z) {
    this.chef.face = Math.atan2(x - this.chef.x, z - this.chef.z);
  }
  /** Walk to a station, press grab there, and check it did what we wanted. */
  *_press(target, check) {
    let s = yield* this._goBeside(target), t = 0;
    this._face(s.wx, s.wz);
    while (!this._aimed(s)) {
      // something else is in the way (a teammate, an item on the floor): hold on a moment
      if ((t += this.dt) > 1.5) throw new Abort('in the way');
      yield;
      s = yield* this._goBeside(target);
      this._face(s.wx, s.wz);
    }
    this.ctrl.grab = true;
    yield;
    yield;
    if (check && !check()) throw new Abort('that did not work');
  }
  /** Stand beside a station holding Use until done (washing up, spraying a fire). */
  *_holdUse(target, done, max, reach = 1) {
    let t = 0;
    while (!done()) {
      const s = yield* this._goBeside(target, reach);
      this._face(s.wx, s.wz);
      this.ctrl.useHeld = true;
      if ((t += this.dt) > max) throw new Abort('taking too long');
      yield;
    }
  }
  /** A station that's busy right now (the only Sealer, say): wait our turn for a while rather than give up. */
  *_waitFor(find, what, max = 10) {
    let t = 0, s;
    while (!(s = find())) {
      if (!this.g.stations.some((x) => x.kind === what)) throw new Abort('no ' + what);
      if ((t += this.dt) > max) throw new Abort(what + ' busy');
      yield;
    }
    return s;
  }
  /**
   * Stand still until something happens (a cup to fizz or seal, an ingredient to be chopped). by: the station to
   * wait beside (a function is fine): if we get moved off (making way for someone), we go back to it.
   */
  *_wait(done, max, keepWorking = null, by = keepWorking) {
    const c = this.chef, t0 = this.g.time;
    while (!done()) {
      if (this.g.time - t0 > max) throw new Abort('waited too long');
      const s = typeof by === 'function' ? by() : by;
      if (s && Math.hypot(s.wx - c.x, s.wz - c.z) > 1.45) {
        yield* this._goBeside(() => (typeof by === 'function' ? by() : by));
        continue;
      }
      if (keepWorking && !this.chef.job) {
        this._face(keepWorking.wx, keepWorking.wz);
        this.ctrl.useHeld = true;
      }
      yield;
    }
  }
  /** Put down whatever we're holding somewhere sensible (keep: something we still need). */
  *_emptyHands(keep = null) {
    const c = this.chef, it = c.held;
    if (!it || (keep && keep(it))) return;
    const dest = this._takeSpot(this._homeFor(it) || this._freeCounter());
    if (dest) {
      yield* this._press(() => dest, () => c.held !== it);
      this._freeSpot(dest); // it's down: the item itself holds the place now
      return;
    }
    this.g.dropToFloor(c);
    yield;
  }
  /** Pick up an item from wherever it sits (a station, or the floor). */
  *_pickUp(it) {
    const c = this.chef;
    this._takeItem(it);
    if (c.held === it) return;
    yield* this._emptyHands();
    const f = this.g.floorItems.find((x) => x.item === it);
    if (f) {
      yield* this._goToPoint(f.x, f.z);
      this.ctrl.grab = true;
      yield;
      yield;
      if (c.held !== it) throw new Abort('missed it');
      return;
    }
    yield* this._press(() => (it.holder && it.holder.kind ? it.holder : null), () => c.held === it);
  }

  // ------------------------------------------------------------ job bodies
  *_fightFire() {
    const g = this.g, c = this.chef;
    if (!(c.held && c.held.kind === 'ext')) {
      const ext = this._near(g.stations.filter((s) => s.item && s.item.kind === 'ext' && !s.fire && this._itemFree(s.item)));
      const f = g.floorItems.find((x) => x.item.kind === 'ext');
      if (!ext && !f) throw new Abort('no extinguisher');
      yield* this._pickUp(ext ? ext.item : f.item);
    }
    for (;;) {
      const fire = this._near(this._stations((s) => s.fire));
      if (!fire) break;
      // the spray reaches about two tiles: any spot in range will do (even for a fire in a corner)
      yield* this._holdUse(() => (fire.fire ? fire : null), () => !fire.fire, 10, 1.9);
    }
    const hook = this._takeSpot(this._near(this._stations((s) => s.kind === 'ext' && !s.item && this._spotFree(s))) || this._freeCounter());
    if (hook) yield* this._press(() => hook, () => !c.held);
  }
  *_offTheHeat(v) {
    yield* this._pickUp(v);
    // onto a free counter (or the floor, when there is none): anywhere but the stove
    yield* this._emptyHands();
  }
  /** Take a finished drink item through its last steps and over the bar. */
  *_serveItem(it, guest) {
    // already in the Carbonator or the Sealer: let it finish there first
    const st = it.holder, at = st && st.kind;
    if ((at === 'carbonator' && !it.fizzy) || (at === 'sealer' && !it.sealed)) {
      yield* this._goBeside(() => (it.holder === st ? st : null));
      yield* this._wait(() => (at === 'sealer' ? it.sealed : it.fizzy) || it.holder !== st, 6);
    }
    yield* this._pickUp(it);
    yield* this._finish(it, RECIPES[it.drink]);
    yield* this._deliver(it, guest);
  }
  /** Pour a finished mix into the right cup, finish it, serve it. */
  *_serveVessel(v, guest, rid = v.result) {
    const g = this.g, c = this.chef, r = RECIPES[rid];
    const fits = (it) => (r.boba ? it.kind === 'bobacup' && !it.drink && !it.sealed : it.kind === 'cup' && it.isCleanEmpty);
    if (!(c.held && fits(c.held))) {
      yield* this._emptyHands();
      yield* this._getCup(fits, r.boba, v.holder);
    }
    const cup = c.held;
    // there as it finishes: wait by it the last moment, then pour straight away
    const brewing = () => !v.result && !v.burnt && (missingFor(r, v.contents) || [0]).length === 0;
    const at = () => (v.holder && v.holder.kind && (v.result === r.id || brewing()) ? v.holder : null);
    const s = yield* this._goBeside(at);
    this._face(s.wx, s.wz);
    yield* this._wait(() => v.result === r.id || !at(), 15, null, at);
    yield* this._press(() => (v.holder && v.holder.kind && v.result === r.id ? v.holder : null), () => cup.drink === r.id);
    yield* this._finish(cup, r);
    yield* this._deliver(cup, guest);
  }
  /** Fizz, pearls, seal and straw: whatever this drink still needs before serving. */
  *_finish(it, r) {
    const c = this.chef;
    this._claim('serve:' + it.uid);
    this._takeItem(it);
    if (it.kind === 'cup' && r.fizz && !it.fizzy) {
      const carb = this._takeSpot(yield* this._waitFor(() => this._near(this._stations((s) => s.kind === 'carbonator' && !s.item && !s.fire && this._spotFree(s))), 'carbonator'));
      yield* this._press(() => carb, () => c.held !== it);
      yield* this._wait(() => it.fizzy, 6);
      yield* this._press(() => carb, () => c.held === it);
      this._freeSpot(carb);
    }
    if (it.kind !== 'bobacup') return;
    if (!it.pearls) {
      const crate = this._near(this._stations((s) => s.kind === 'crate' && s.ingId === 'pearls' && this._stepsTo(s) < Infinity));
      if (crate) yield* this._press(() => crate, () => it.pearls);
      else {
        // the pearls are across the way: set the cup down, have a teammate throw us some, scoop them in
        const spot = this._freeCounter(c.x, c.z, null, true);
        if (!spot) throw new Abort('no pearls');
        if (c.held === it) yield* this._press(() => spot, () => c.held !== it);
        yield* this._requestSupply('pearls');
        if (!(c.held && c.held.id === 'pearls')) throw new Abort('no pearls');
        yield* this._press(() => (spot.item === it ? spot : null), () => it.pearls);
        yield* this._press(() => (spot.item === it ? spot : null), () => c.held === it);
        this._freeSpot(spot);
      }
    }
    if (!it.sealed) {
      const sealer = this._takeSpot(yield* this._waitFor(() => this._near(this._stations((s) => s.kind === 'sealer' && (s.item === it || (!s.item && this._spotFree(s))) && !s.fire)), 'sealer'));
      if (c.held === it) yield* this._press(() => sealer, () => c.held !== it);
      yield* this._wait(() => it.sealed, 6);
      yield* this._press(() => sealer, () => c.held === it);
      this._freeSpot(sealer);
    }
    if (!it.straw) {
      const straws = this._near(this._stations((s) => s.kind === 'straws' && this._stepsTo(s) < Infinity));
      if (straws) yield* this._press(() => straws, () => it.straw);
      else {
        // the straw holder is across the way: set the cup down, catch a straw from a teammate, stab it in
        if (!this.g.stations.some((s) => s.kind === 'straws')) throw new Abort('no straws');
        const spot = this._freeCounter(c.x, c.z, null, true);
        if (!spot) throw new Abort('no straws');
        if (c.held === it) yield* this._press(() => spot, () => c.held !== it);
        yield* this._requestSupply('straw');
        if (!(c.held && c.held.kind === 'straw')) throw new Abort('no straws');
        yield* this._press(() => (spot.item === it ? spot : null), () => it.straw);
        yield* this._press(() => (spot.item === it ? spot : null), () => c.held === it);
        this._freeSpot(spot);
      }
    }
  }
  /**
   * Over the bar to the guest (or the next one who wants it, if they left). On the way, the delivery share-out has the
   * final say: it can swap us to another guest no other bot has taken, or (a nearer chef now carrying it to them) call
   * us off.
   */
  *_deliver(it, guest) {
    const c = this.chef;
    let q = guest.state === 'wait' ? guest : this._guestFor(it.drink);
    if (!q) return yield* this._emptyHands();
    const bar = () => {
      const d = this.g.deliveries().get(c);
      if (d && !d.guest) q = null;
      else if (d && !this._taken('guest:' + d.guest.id)) q = d.guest;
      return q && q.state === 'wait' ? this._barOf(q) : null;
    };
    yield* this._press(bar, () => c.held !== it);
  }
  *_dump(it) {
    const c = this.chef;
    yield* this._pickUp(it);
    const bin = this._near(this._stations((s) => s.kind === 'bin'));
    if (!bin) throw new Abort('no zapper');
    yield* this._press(() => bin, () => c.held !== it || !isSludgy(it));
    // back where it belongs (an emptied kettle onto a free base, a cup onto the rack)
    if (c.held === it) yield* this._emptyHands();
  }
  *_collectDirty() {
    const c = this.chef;
    yield* this._emptyHands((it) => it.kind === 'cup' && it.dirty);
    for (;;) {
      const s = this._near(this._stations((x) => x.item && x.item.kind === 'cup' && x.item.dirty));
      if (!s) break;
      const before = s.item;
      yield* this._press(() => (s.item === before ? s : null), () => s.item !== before);
    }
    const sink = this._near(this.g.sinks);
    if (!sink || !c.held) return;
    this._claim('wash:' + sink.x + ',' + sink.y); // we'll wash these ourselves
    yield* this._press(() => sink, () => !c.held);
    yield* this._holdUse(() => sink, () => sink.dirty <= 0, 40);
  }
  *_wash(sink) {
    yield* this._emptyHands();
    yield* this._holdUse(() => sink, () => sink.dirty <= 0, 40);
  }
  *_tidyFloor(it) {
    yield* this._pickUp(it);
    yield* this._emptyHands();
  }
  *_putAway(v) {
    const home = this._homeFor(v);
    if (!home) return;
    yield* this._pickUp(v);
    yield* this._press(() => (!home.item ? home : null), () => this.chef.held !== v);
  }
  /** A tile to stand on to use this station (for measuring walks from it). */
  _standOf(s) {
    return this._standTiles(s, 1).find(([x, y]) => this._walk(x, y)) || null;
  }
  /** Steps to fetch from station s and then carry on to dest (plus any extra, in steps): the one to pick is the lowest. */
  _tripVia(s, dest, extra = 0) {
    const a = this._stepsTo(s);
    if (a === Infinity) return Infinity;
    if (!dest) return a + extra;
    const from = this._standOf(s);
    const b = from ? this._stepsTo(dest, from) : Infinity;
    return a + (b === Infinity ? 40 : b) + extra;
  }
  /** Pick up the cup that gets us to dest soonest: a loose one on the way, or one off the rack / stack. */
  *_getCup(fits, boba, dest) {
    const g = this.g, c = this.chef;
    let best = null, bc = Infinity;
    const consider = (s, grab, extra = 0) => {
      const k = this._tripVia(s, dest, extra);
      if (k < bc) [best, bc] = [{ s, grab }, k];
    };
    const pearlTrip = boba && !g._noNew ? 4 : 0; // an empty boba cup still needs its pearls scooped in
    for (const s of this._stations((x) => x.item && fits(x.item) && x.kind !== 'bar' && this._itemFree(x.item))) consider(s, false, s.item.pearls ? 0 : pearlTrip);
    for (const s of boba ? this._stations((x) => x.kind === 'cups') : g.racks.filter((x) => x.clean > 0)) consider(s, true, pearlTrip);
    if (!best) throw new Abort(boba ? 'no boba cups' : 'no clean cups');
    if (best.grab) yield* this._press(() => best.s, () => c.held && fits(c.held));
    else yield* this._pickUp(best.s.item);
  }
  /** Get one ingredient, prepared and ready, into our hands. */
  *_fetch(id, dest = null) {
    const g = this.g, c = this.chef;
    if (c.held && c.held.kind === 'ing' && c.held.id === id) {
      if (!c.held.ready) yield* this._prepHeld();
      return;
    }
    yield* this._emptyHands();
    const reach = (st) => this._stepsTo(st) < Infinity;
    // whichever gets it where it's going soonest: walking there and on, plus any chopping or grinding still to do
    const def = INGREDIENTS[id], prepSteps = def.prep ? (def.prep === 'chop' ? 1.7 : 2.0) * 4 : 0;
    let pick = null, pc = Infinity;
    const consider = (kind, st, extra) => {
      const k = this._tripVia(st, dest, extra);
      if (k < pc) [pick, pc] = [{ kind, st }, k];
    };
    for (const st of this._stations((x) => x.item && x.item.kind === 'ing' && x.item.id === id && x.kind !== 'conveyor' && this._itemFree(x.item))) {
      const it = st.item;
      if (it.ready) consider('ready', st, 0);
      // one half done on a board (someone got called away mid-chop), or a raw one lying about: finish that, don't start afresh
      else if (!g.chefs.some((o) => o.job === st)) consider('unfinished', st, st.kind === def.prep ? (1 - it.prog) * prepSteps : prepSteps + 3);
    }
    for (const st of this._stations((x) => x.kind === 'crate' && x.ingId === id)) consider('crate', st, def.prep ? prepSteps + 3 : 0);
    // a spare one on the floor (dropped when plans changed and the counters were full): use it up, so spares never pile up
    for (const f of g.floorItems) {
      const it = f.item;
      if (it.kind !== 'ing' || it.id !== id || !this._itemFree(it)) continue;
      const at = g.worldToTile(f.x, f.z), p = this._path(new Set([at[0] + ',' + at[1]]));
      if (!p) continue;
      const on = dest ? this._stepsTo(dest, at) : 0;
      const k = p.length - 1 + (on === Infinity ? 40 : on) + (it.ready ? 0 : def.prep ? prepSteps + 3 : 0);
      if (k < pc) [pick, pc] = [{ kind: 'floor', it }, k];
    }
    if (pick && pick.kind === 'floor') {
      yield* this._pickUp(pick.it);
      if (!c.held.ready) yield* this._prepHeld();
      return;
    }
    if (pick && pick.kind === 'ready') {
      yield* this._pickUp(pick.st.item);
      return;
    }
    if (pick && pick.kind === 'unfinished') {
      yield* this._finishPrep(pick.st);
      return;
    }
    const crate = pick && pick.kind === 'crate' ? pick.st : null;
    const belt = g.stations.find((s) => s.kind === 'conveyor' && s.item && s.item.kind === 'ing' && s.item.id === id && this._itemFree(s.item) && reach(s));
    if (crate) yield* this._press(() => crate, () => c.held && c.held.id === id);
    else if (belt) yield* this._grabFromBelt(this._takeItem(belt.item));
    else if (g.stations.some((s) => s.kind === 'crate' && s.ingId === id)) {
      // out of reach from here (the far side of a gap or a wall): a teammate over there throws us one
      yield* this._requestSupply(id);
      if (!(c.held && c.held.kind === 'ing' && c.held.id === id)) throw new Abort('no supply');
      return;
    } else throw new Abort('none to be had');
    if (!c.held.ready) yield* this._prepHeld();
  }
  /**
   * Ask a free teammate who can reach the ingredient to fetch it (prepared) and throw it to us. We pick a pair of
   * spots, theirs and ours, that a throw will surely connect, walk to ours and wait there to catch it.
   */
  *_requestSupply(id) {
    const g = this.g, c = this.chef;
    if (delegateTasks(g).some((t) => t.id === 'runner') && !this._use('runner')) throw new Abort('runner off');
    // where one comes from: its crate (or, for a straw, the straw holder)
    const crates = g.stations.filter((s) => (id === 'straw' ? s.kind === 'straws' : s.kind === 'crate' && s.ingId === id));
    const mine = this._reachNear(10);
    const dummy = id === 'straw' ? { kind: 'straw' } : { kind: 'ing', id };
    let best = null;
    for (const o of g.chefs) {
      const b = o.bot;
      const myPrio = this.job ? this.job.prio : 50;
      if (!b || o === c || o.respawn > 0 || b.catchFor || b.yieldGen || b.supplyFor || b.awaiting || (b.job && !b.job.soft && b.job.prio > myPrio)) continue;
      const from = g.worldToTile(o.x, o.z);
      let crate = null, toCrate = Infinity;
      for (const s of crates) {
        const d = this._stepsTo(s, from);
        if (d < toCrate) [crate, toCrate] = [s, d];
      }
      if (!crate) continue;
      // where they could throw from: near the crate (and whatever board they'd prep on), on their side
      const [cx, cy] = this._standTiles(crate, 1).find(([x, y]) => this._walk(x, y)) || [crate.x, crate.y];
      const theirs = this._reachNear(12, [cx, cy]);
      const pairs = [];
      for (const [kr, dr] of mine)
        for (const [ks, ds] of theirs) {
          const [rx, ry] = kr.split(',').map(Number), [sx, sy] = ks.split(',').map(Number);
          const d = Math.hypot(rx - sx, ry - sy);
          if (d < 1.5 || d > 4.4 || mine.has(ks)) continue; // across the divide: they can't just walk it over
          pairs.push([Math.max(dr, toCrate + ds), rx, ry, sx, sy]);
        }
      pairs.sort((a, b2) => a[0] - b2[0]);
      for (const [cost, rx, ry, sx, sy] of pairs.slice(0, 60)) {
        if (best && cost >= best.cost) break;
        const [rwx, rwz] = g.tileToWorld(rx, ry), [swx, swz] = g.tileToWorld(sx, sy);
        const who = { thrower: o, catcher: { x: rwx, z: rwz, chef: c } };
        if (this._sureThrow(swx, swz, rwx, rwz, dummy, (r) => r.chef === c, c, who)) {
          best = { cost, o, catchAt: [rx, ry], throwFrom: [sx, sy] };
          break;
        }
      }
    }
    if (!best) throw new Abort('no one to throw it');
    const o = best.o;
    o.bot.supplyFor = { to: c, id, spot: best.throwFrom, prio: this.job ? this.job.prio + 2 : 60 };
    this.awaiting = o;
    try {
      // to our catching spot, then wait there with empty hands
      const [rx, ry] = best.catchAt;
      const [wx, wz] = g.tileToWorld(rx, ry);
      yield* this._goToPoint(wx, wz);
      for (let t = 0; t < 25; t += this.dt) {
        if (c.held) return;
        if (!o.bot.supplyFor) break; // they gave up
        // hold the spot (someone may have nudged us)
        const d = Math.hypot(wx - c.x, wz - c.z);
        if (d > 0.1) {
          this.ctrl.mx = ((wx - c.x) / d) * Math.min(1, d * 4);
          this.ctrl.mz = ((wz - c.z) / d) * Math.min(1, d * 4);
        }
        this._face(o.x, o.z);
        yield;
      }
    } finally {
      this.awaiting = null;
      if (o.bot.supplyFor && o.bot.supplyFor.to === c) o.bot.supplyFor = null;
    }
  }
  /** Finish an ingredient someone left unprepared: on its own board, just carry on chopping there; elsewhere, take it to one. Ends holding it. */
  *_finishPrep(st) {
    const c = this.chef, it = this._takeItem(st.item);
    if (st.kind === it.def.prep) {
      this._takeSpot(st);
      const here = () => (st.item === it ? st : null);
      yield* this._holdUse(here, () => it.ready, 12);
      yield* this._press(here, () => c.held === it);
      this._freeSpot(st);
      return;
    }
    yield* this._pickUp(it);
    yield* this._prepHeld();
  }
  /** Finish it on its board, then leave it ready on a counter (freeing the board). */
  *_finishPrepped(st) {
    yield* this._emptyHands();
    yield* this._finishPrep(st);
    const spot = this._freeCounter(this.chef.x, this.chef.z, null, true);
    if (spot) yield* this._press(() => spot, () => !this.chef.held);
  }
  /** Chop or grind what we're holding, then pick it back up. */
  *_prepHeld() {
    const c = this.chef, it = c.held;
    this._takeItem(it); // ours while this job lasts (a stroll to make way for someone doesn't hand it over)
    const board = this._takeSpot(yield* this._waitFor(() => this._near(this._stations((s) => s.kind === it.def.prep && !s.item && !s.fire && this._spotFree(s))), it.def.prep, 8));
    yield* this._press(() => board, () => c.held !== it);
    yield* this._wait(() => it.ready || board.item !== it, 10, board);
    yield* this._press(() => (board.item === it ? board : null), () => c.held === it);
    this._freeSpot(board);
  }
  *_prepOne(id) {
    const c = this.chef;
    yield* this._emptyHands();
    const crate = this._near(this._stations((s) => s.kind === 'crate' && s.ingId === id));
    const belt = this.g.stations.find((s) => s.kind === 'conveyor' && s.item && s.item.kind === 'ing' && s.item.id === id && this._itemFree(s.item));
    if (crate) yield* this._press(() => crate, () => c.held && c.held.id === id);
    else if (belt) yield* this._grabFromBelt(this._takeItem(belt.item));
    else throw new Abort('none to be had');
    yield* this._prepHeld();
    // free the board for the next one, if there's a counter for it
    const spot = this._freeCounter(this.chef.x, this.chef.z, null, true);
    if (spot) yield* this._press(() => spot, () => !c.held);
  }
  /** Bring one ingredient to a kettle or jug (and put it in: by hand, by a throw, or by passing it to a bot nearer). */
  *_addOne(v, id, rid) {
    const c = this.chef, recipe = typeof rid === 'string' ? RECIPES[rid] : rid; // (or a shared start: see _sharedStart)
    const wanted = () => !!(v.holder && v.holder.kind && !v.burnt && v.result !== 'sludge' && (missingFor(recipe, v.contents) || []).includes(id));
    if (!wanted()) throw new Abort('not needed');
    // thinking of both: the kettle or blender, and what's going in it
    c.thought = `img:${v.app}|img:ing:${id}:raw`;
    yield* this._fetch(id, v.holder);
    // someone else may have put it in meanwhile: don't add a second one (that could spoil the mix)
    if (!wanted()) throw new Abort('already added');
    yield* this._putIn(v, wanted);
  }
  // ------------------------------------------------------------ throwing
  /**
   * Where a throw from (x, z) facing (dx, dz) ends up, following the game's own projectile rules (other chefs stand still).
   * {chef} caught, {st, add} lands in a kettle/jug, {st} lands on a station, {floor}, {lost}. near: closest another chef came.
   */
  _simThrow(x, z, dx, dz, item, step = 1 / 60, who = null) {
    const g = this.g;
    const thrower = (who && who.thrower) || this.chef, catcher = who && who.catcher;
    let px = x + dx * 0.4, py = 1.0, pz = z + dz * 0.4, vy = 2.2, t = 0;
    const others = g.chefs.filter((o) => o !== thrower && o !== (catcher && catcher.chef) && o.respawn <= 0 && o.falling <= 0);
    const near = new Map();
    for (let i = 0; i < 400; i++) {
      t += step;
      vy -= 14 * step;
      px += dx * 7.5 * step;
      py += vy * step;
      pz += dz * 7.5 * step;
      if (t >= 0.08 && py < 1.6)
        for (const o of others) {
          // where they'll be by then if they keep going (people and bots can also turn: see near)
          const d = Math.hypot(o.x + o.vx * t - px, o.z + o.vz * t - pz);
          // clearance after allowing for a change of direction (a chef covers up to 4 tiles a second)
          near.set(o, Math.min(near.get(o) ?? 9, Math.hypot(o.vx, o.vz) < 0.3 ? d : d - 0.8 * t));
          if (d < 0.55 && !o.held) return { chef: o, near, t };
        }
      // someone who'll be standing still at a spot, hands empty
      if (catcher && t >= 0.08 && py < 1.6 && Math.hypot(catcher.x - px, catcher.z - pz) < 0.45) return { chef: catcher.chef, near, t };
      const [tx, ty] = g.worldToTile(px, pz);
      const tile = g.tileAt(tx, ty);
      if (!tile) return { lost: true, near, t };
      if ((tile.ch === ' ' && py < 0.2) || (tile.ch === '=' && !g.bridgeOpen(tx, ty) && py < 0.2)) return { lost: true, near, t };
      const st = tile.st;
      if ((st || tile.ch === 'W' || tile.ch === '~' || tile.ch === 'g') && py < COUNTER_H + 0.1) {
        const v = st && !st.fire && st.item && st.item.kind === 'vessel' ? st.item : null;
        return { st, add: !!(v && item.kind === 'ing' && v.canAdd(item)), near, t };
      }
      if (py <= 0.05) return { floor: true, near, t };
    }
    return { lost: true, near, t };
  }
  /** A throw we can count on: the same result at any frame rate, and nobody else anywhere near its path. */
  _sureThrow(x, z, tx, tz, item, ok, keepClear = null, who = null) {
    const d = Math.hypot(tx - x, tz - z);
    if (d < 0.6 || d > 4.6) return false;
    const dx = (tx - x) / d, dz = (tz - z) / d;
    for (const step of [1 / 30, 1 / 60, 1 / 144]) {
      const r = this._simThrow(x, z, dx, dz, item, step, who);
      if (!ok(r)) return false;
      for (const [o, m] of r.near) if (o !== keepClear && o !== r.chef && m < 0.75) return false;
    }
    return true;
  }
  /** A point to aim at on this station (its middle, or a little to one side) that a throw from (x, z) surely lands in. */
  _aimAt(x, z, st, it) {
    const ok = (r) => r.st === st && r.add;
    for (const [ox, oz] of [[0, 0], [0.18, 0], [-0.18, 0], [0, 0.18], [0, -0.18], [0.3, 0], [-0.3, 0], [0, 0.3], [0, -0.3]])
      if (this._sureThrow(x, z, st.wx + ox, st.wz + oz, it, ok)) return [st.wx + ox, st.wz + oz];
    return null;
  }
  /** Tiles we can walk to on our side (no teleporters), with how many steps each takes. */
  _reachNear(maxD, from = null) {
    const g = this.g;
    const [sx, sy] = from || g.worldToTile(this.chef.x, this.chef.z);
    const dist = new Map([[sx + ',' + sy, 0]]);
    const q = [[sx, sy]];
    while (q.length) {
      const [x, y] = q.shift();
      const d = dist.get(x + ',' + y);
      if (d >= maxD) continue;
      for (const [dx, dy] of DIRS) {
        const nx = x + dx, ny = y + dy, k = nx + ',' + ny;
        if (dist.has(k) || !this._walk(nx, ny) || this._avoiding(nx, ny)) continue;
        dist.set(k, d + 1);
        q.push([nx, ny]);
      }
    }
    return dist;
  }
  /** Steps from a tile to beside a station (Infinity: no way). */
  _stepsTo(s, from = null) {
    const goals = new Set();
    for (const [x, y] of this._standTiles(s, 1)) if (this._walk(x, y)) goals.add(x + ',' + y);
    const p = goals.size ? this._path(goals, from) : null;
    return p ? p.length - 1 : Infinity;
  }
  /**
   * The quickest way to get what we're holding into this kettle/jug: carry it (null), throw it in from a spot
   * ({spot, direct}), or throw it to a free bot who's nearer ({spot, to}). Throws only when they're sure.
   */
  _deliveryPlan(v) {
    const g = this.g, c = this.chef, it = c.held, st = v.holder;
    if (!it || it.kind !== 'ing' || !st || !st.kind) return null;
    const walk = this._stepsTo(st);
    const spots = this._reachNear(8);
    let best = null, bestCost = walk - 0.3; // worth it when it saves about a tile of walking
    // throw it straight in: from right here...
    if (this._aimAt(c.x, c.z, st, it)) return { here: true, direct: true };
    // ...or from a spot on the way
    for (const [k, d] of spots) {
      if (d + 0.4 >= bestCost) continue;
      const [x, y] = k.split(',').map(Number);
      const [wx, wz] = g.tileToWorld(x, y);
      if (this._aimAt(wx, wz, st, it)) {
        bestCost = d + 0.4;
        best = { spot: [x, y], direct: true };
      }
    }
    // or pass it to a teammate who'll get it there sooner
    const prio = this.job ? this.job.prio : 0;
    for (const o of g.chefs) {
      const b = o.bot;
      if (!b || o === c || o.held || o.respawn > 0 || b.catchFor || b.yieldGen || !b._couldCatch(prio)) continue;
      const theirs = this._stepsTo(st, g.worldToTile(o.x, o.z));
      if (theirs + 2 >= bestCost) continue;
      for (const [k, d] of spots) {
        const cost = Math.max(d, 0) + 2 + theirs;
        if (cost >= bestCost) continue;
        const [x, y] = k.split(',').map(Number);
        const [wx, wz] = g.tileToWorld(x, y);
        if (this._sureThrow(wx, wz, o.x, o.z, it, (r) => r.chef === o, o)) {
          bestCost = cost;
          best = { spot: [x, y], to: o };
        }
      }
    }
    return best;
  }
  /**
   * Whether this bot could stop to catch a teammate's pass on a job of this importance: it's free, on a soft job, or on
   * a job it can drop with empty hands (fetching, prep, boba cups, belt catching) that matters no more than the pass.
   * Whoever catches takes the delivery over, and the job they dropped goes back to the kitchen for someone else.
   */
  _couldCatch(prio) {
    const j = this.job;
    return !j || j.soft || (j.prio <= prio && this._droppable(j));
  }
  /** Get the ingredient in our hands into the kettle/jug: by hand, or with a throw when that's quicker. */
  *_putIn(v, wanted) {
    const g = this.g, c = this.chef, it = c.held;
    let planT = 0, plan = null, walkGen = null, carry = false;
    for (let tries = 0; ; ) {
      if (!wanted() || c.held !== it) throw new Abort('not needed now');
      const st = v.holder;
      if (!st || !st.kind) throw new Abort('mix gone');
      if ((planT -= this.dt) <= 0) {
        planT = 0.25;
        const p = carry ? null : this._deliveryPlan(v);
        if (!p || !plan || p.to !== plan.to || p.direct !== plan.direct || p.here !== plan.here) {
          this._cancelPass();
          plan = p;
          walkGen = null;
          if (plan && plan.to) {
            // ask them to stay put and get ready to catch
            this.passTo = plan.to;
            plan.to.bot.catchFor = { from: c, until: g.time + 8, icon: `img:${v.app}|img:ing:${it.id}:raw` };
          }
        }
      }
      if (!plan) {
        // carry it there (a few steps at a time, so a better idea can still come along)
        walkGen = walkGen || this._goBeside(() => (v.holder && v.holder.kind ? v.holder : null));
        let s = null;
        for (let t = 0; t < 0.25 && !s; t += this.dt) {
          const r = walkGen.next();
          if (r.done) s = r.value;
          else yield;
        }
        if (!s) continue;
        walkGen = null;
        const n = v.contents.length;
        yield* this._press(() => (v.holder && v.holder.kind ? v.holder : null), () => v.contents.length > n);
        return;
      }
      // walk to the throwing spot (unless it's right here)
      const [sx, sy] = plan.here ? g.worldToTile(c.x, c.z) : plan.spot;
      const [wx, wz] = plan.here ? [c.x, c.z] : g.tileToWorld(sx, sy);
      if (!plan.here && Math.hypot(c.x - wx, c.z - wz) > 0.12) {
        const [tx, ty] = g.worldToTile(c.x, c.z);
        if (tx === sx && ty === sy) {
          const dx = wx - c.x, dz = wz - c.z, d = Math.hypot(dx, dz);
          this.ctrl.mx = (dx / d) * Math.min(1, d * 4);
          this.ctrl.mz = (dz / d) * Math.min(1, d * 4);
          yield;
        } else yield* this._stepTowards(() => new Set([sx + ',' + sy]), 'throw' + sx + ',' + sy);
        continue;
      }
      // stop, settle, check the throw once more from exactly here, then let go
      if (c.dashT > 0) {
        yield; // mid-dash: let it finish first (a dash would throw the aim off)
        continue;
      }
      if (!plan.here) for (let i = 0; i < 2; i++) yield;
      const target = plan.direct ? st : plan.to;
      // where we'll be this frame (we let go of the stick, but still drift a touch)
      const px = c.x + c.vx * 0.6 * this.dt, pz = c.z + c.vz * 0.6 * this.dt;
      const aim = plan.direct ? this._aimAt(px, pz, st, it) : this._sureThrow(px, pz, plan.to.x, plan.to.z, it, (r) => r.chef === plan.to, plan.to) ? [plan.to.x, plan.to.z] : null;
      if (!aim) {
        // someone's in the way now: try again in a moment, and after a few goes just carry it
        if (++tries > 2) carry = true;
        this._cancelPass();
        plan = null;
        planT = carry ? 0 : 0.3;
        continue;
      }
      const [ax, az] = aim;
      this.chef.face = Math.atan2(ax - px, az - pz);
      const n0 = v.contents.length;
      this.ctrl.use = true; // throw
      if (plan.direct) {
        (g._inFlight = g._inFlight || []).push({ uid: v.uid, id: it.id, until: g.time + 1 });
        g._throwsIn = (g._throwsIn || 0) + 1;
        yield;
        return;
      }
      yield;
      // watch it fly
      for (let t = 0; g.projectiles.some((p) => p.item === it) && t < 1.5; t += this.dt) yield;
      if (plan.direct && v.contents.length > n0) {
        g._throwsIn = (g._throwsIn || 0) + 1;
        return;
      }
      // caught by someone: if it's a bot (the one we meant, or not), it takes over from here
      const o = it.holder && it.holder.bot ? it.holder : null;
      if (!o) throw new Abort(plan.direct ? 'throw missed' : 'pass dropped');
      if (o !== target) g._strayCatches = (g._strayCatches || 0) + 1;
      // they've got it: the job is theirs now
      const j = this.job;
      const spec = { key: j.key, prio: j.prio, icon: j.icon, max: 60, claims: j.claims.filter((k) => k !== j.key), soft: false, meta: j.meta, run: () => o.bot._addOne(v, it.id, RECIPES[v.plan] ? v.plan : this._recipeOf(v, it.id)) };
      this.passTo = null;
      this._stop();
      o.bot.catchFor = null;
      o.bot._begin(spec);
      g._passes = (g._passes || 0) + 1;
      return;
    }
  }
  /** Fetch an ingredient a teammate across the way asked for, and throw it to them where they wait. */
  *_supply(sup) {
    const g = this.g, c = this.chef, to = sup.to;
    const live = () => this.supplyFor === sup && to.bot && to.bot.awaiting === c;
    try {
      if (sup.id === 'straw') {
        yield* this._emptyHands();
        const holder = this._near(this._stations((s) => s.kind === 'straws' && this._stepsTo(s) < Infinity));
        if (!holder) throw new Abort('no straws');
        yield* this._press(() => holder, () => c.held && c.held.kind === 'straw');
      } else yield* this._fetch(sup.id);
      const it = c.held;
      const [sx, sy] = sup.spot;
      const [wx, wz] = g.tileToWorld(sx, sy);
      yield* this._goToPoint(wx, wz);
      for (let t = 0; ; t += this.dt) {
        if (!live()) throw new Abort('not wanted now');
        if (t > 12) throw new Abort('could not throw');
        // they're in place and still: check once more from exactly here, then throw
        const still = Math.hypot(to.vx, to.vz) < 0.3;
        if (still && this._sureThrow(c.x, c.z, to.x, to.z, it, (r) => r.chef === to, to)) {
          this._face(to.x, to.z);
          this.ctrl.use = true;
          yield;
          for (let k = 0; g.projectiles.some((p) => p.item === it) && k < 1.5; k += this.dt) yield;
          if (to.held === it) g._supplies = (g._supplies || 0) + 1;
          else throw new Abort('supply missed');
          return;
        }
        const d = Math.hypot(wx - c.x, wz - c.z);
        if (d > 0.1) {
          this.ctrl.mx = ((wx - c.x) / d) * Math.min(1, d * 4);
          this.ctrl.mz = ((wz - c.z) / d) * Math.min(1, d * 4);
        }
        yield;
      }
    } finally {
      if (this.supplyFor === sup) this.supplyFor = null;
    }
  }
  /** The recipe a mix is heading for (for a teammate taking over). */
  _recipeOf(v, id) {
    const q = this._waiting().find((w) => w.recipe.app === v.app && (missingFor(w.recipe, v.contents) || []).includes(id));
    return q ? q.recipe.id : null;
  }
  /** Add ingredients to a mix until it becomes the recipe (one: just the next missing one). */
  *_fill(v, recipe, one) {
    const c = this.chef;
    for (;;) {
      if (!v.holder || !v.holder.kind || v.burnt || v.result === 'sludge') throw new Abort('mix gone');
      const miss = missingFor(recipe, v.contents);
      if (!miss) throw new Abort('mix changed');
      if (!miss.length) return;
      // water last, so a partial mix doesn't finish as some other drink on the way
      const id = miss.slice().sort((a, b) => (a === 'water') - (b === 'water'))[0];
      // thinking of both: the ingredient, and the kettle or blender it's for
      c.thought = `img:${v.app}|img:ing:${id}:raw`;
      yield* this._fetch(id);
      // someone else may have put it in meanwhile: don't add a second one (that could spoil the mix)
      const now = missingFor(recipe, v.contents);
      if (!now || !now.includes(id)) throw new Abort('already added');
      const n = v.contents.length;
      yield* this._press(() => (v.holder && v.holder.kind ? v.holder : null), () => v.contents.length > n);
      if (one) return;
    }
  }
  *_bobaCupReady(sealer) {
    const c = this.chef;
    yield* this._emptyHands();
    const stack = this._near(this._stations((s) => s.kind === 'cups'));
    yield* this._press(() => stack, () => c.held && c.held.kind === 'bobacup');
    const crate = this._near(this._stations((s) => s.kind === 'crate' && s.ingId === 'pearls'));
    if (!crate) throw new Abort('no pearls');
    yield* this._press(() => crate, () => c.held.pearls);
    const spot = this._freeCounter(sealer.wx, sealer.wz, null, true);
    if (!spot) return yield* this._emptyHands();
    yield* this._press(() => (!spot.item ? spot : null), () => !c.held);
  }
  *_grabFromBelt(it) {
    const c = this.chef;
    const where = () => this.g.stations.find((s) => s.kind === 'conveyor' && s.item === it) || null;
    yield* this._goBeside(where);
    const s = where();
    if (!s) throw new Abort('rode away');
    const [x, z] = this.g._beltSpot(s);
    this._face(x, z);
    this.ctrl.grab = true;
    yield;
    yield;
    if (c.held !== it) throw new Abort('missed it');
  }
  *_catch(it) {
    yield* this._emptyHands();
    yield* this._grabFromBelt(it);
    const spot = this._freeCounter(this.chef.x, this.chef.z, null, true);
    if (spot) yield* this._press(() => (!spot.item ? spot : null), () => !this.chef.held);
  }
}